/**
 * v1.4.0 Boutique Mode — foundation UI (servicios, productos, agenda, historial).
 *
 * Pantallas de CRUD list+form mínimas viables, autocontenidas (importadas por
 * Dashboard.tsx). NO incluyen POS, calendario visual ni reminders engine — eso
 * es v1.4.1+.
 *
 * Limitación de foundation (DECISIÓN PENDIENTE): el "cliente" se captura como UUID
 * de `contacts` en un input de texto, porque hoy no existe un endpoint de listado
 * de contactos. Un picker real llega en v1.4.1. El backend valida que el UUID
 * exista en el tenant (400 si no).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, Mail, CalendarDays, List, ChevronLeft, ChevronRight, Bell, Check, Clock, Archive, ArchiveRestore } from 'lucide-react';
import { api } from '../lib/api';
import { altUnitOf, recipeWarnings } from './recipeGuard';
import { RecipeLineRow } from './RecipeLineRow';
import { StockRepartoModal } from './StockRepartoModal';
import { BranchTransfers, BranchPricing, ProductBranchPriceModal, useHasMultipleBranches } from '../components/BranchInventoryTools';
import { useActiveWriteBranch } from '../lib/useActiveWriteBranch';
// v1.4.1 — Contact picker reemplaza el input de UUID crudo.
import { ContactPicker } from '../components/ContactPicker';
// #305-followup P3 — alta rápida de cliente inline desde "Nueva cita".
import { useContacts } from '../contacts/useContacts';
// #307 — agenda unificada: se reusan los modales de gestión del motor de booking
// (misma superficie que la vieja pestaña "Reservas") para gestionar una reserva
// en línea desde su sesión, sin duplicar lógica. Wellness sigue montando
// BookingManagerView directo en su tab de Clases (no toca esto).
import {
  OfferingsModal, SlotModal, InstructorsModal, SlotDetailModal,
  type Slot, type Offering, type Instructor,
} from '../booking/BookingViews';
// Gating en la UI (doble enforcement con el backend), compartido por:
//  - feat/roles-cajero-contador: Cajero/Contador VEN el catálogo pero NO editan
//    costo/precio/stock (el backend ya 403ea; aquí ocultamos los controles).
//  - V54 insumos-recetas: mismos gates para insumos y el botón de Receta.
// SUPER_USER bypassa (igual que el PermissionsGuard del backend).
import { useAuth } from '../auth/AuthProvider';
import { newEditSession, fmtInsumoQty, roundQtyForUnit, type EditSession } from './inlineEditOnce';

function can(user: any, perm: string): boolean {
  if (!user) return false;
  if (user.role === 'SUPER_USER') return true;
  return Array.isArray(user.permissions) && user.permissions.includes(perm);
}

const money = (v: any, c?: string) =>
  `${c ?? 'MXN'} ${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function L({ label, children }: { label: string; children: any }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">{label}</span>
      {children}
    </label>
  );
}

// #249 — el panel se limita a la altura del viewport (`max-h-[90vh]`) con el
// CUERPO scrolleable y el header/footer fijos (`shrink-0`). Antes crecía sin
// límite: en tablets el formulario largo (variantes, precio por sucursal…)
// desbordaba la pantalla SIN scroll y "Guardar" quedaba inalcanzable. El botón
// de acción va en `footer` (barra sticky, siempre visible); como vive fuera del
// <form>, se ata a él con `form="<id>"` en el submit.
function Modal({
  title, onClose, children, footer, maxWidth = 'max-w-md',
}: { title: string; onClose: () => void; children: any; footer?: any; maxWidth?: string }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className={`flex max-h-[90vh] w-full ${maxWidth} flex-col overflow-hidden rounded-2xl bg-white shadow-xl`}>
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && <div className="shrink-0 border-t border-slate-100 px-6 py-3">{footer}</div>}
      </div>
    </div>
  );
}

function Header({ title, subtitle, action }: { title: string; subtitle: string; action?: any }) {
  return (
    <div className="mb-5 flex items-end justify-between">
      <div>
        <h2 className="font-serif text-2xl tracking-tight text-slate-900">{title}</h2>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

// ---------------------------------------------------------------------------
// Claridad de IVA en el catálogo. El modelo es correcto (price_includes_tax):
// B2C captura el precio AL PÚBLICO (con IVA) → el sistema lo desglosa hacia atrás;
// B2B captura precio + IVA. Aquí damos toggle + preview en vivo + costo (margen).
// ---------------------------------------------------------------------------
const num2 = (v: any) => (Number(v) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ivaBreakdown(price: number, includesTax: boolean, rate: number) {
  const r = (Number(rate) || 0) / 100;
  if (includesTax) {
    const total = price; const subtotal = r > 0 ? total / (1 + r) : total;
    return { subtotal, iva: total - subtotal, total };
  }
  const subtotal = price; const iva = subtotal * r;
  return { subtotal, iva, total: subtotal + iva };
}

/** Bloque reutilizable: toggle precio-con-IVA / +IVA + preview en vivo + costo. */
function IvaFields({ price, cost, includesTax, taxRate, onCost, onIncludes }: {
  price: string; cost: string; includesTax: boolean; taxRate: number;
  onCost: (v: string) => void; onIncludes: (v: boolean) => void;
}) {
  const p = Number(price) || 0;
  const b = ivaBreakdown(p, includesTax, taxRate);
  const costN = Number(cost) || 0;
  const margin = costN > 0 ? b.subtotal - costN : null;
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
      <div className="flex flex-col gap-1.5 text-xs text-slate-700">
        <label className="flex items-center gap-2">
          <input type="radio" checked={includesTax} onChange={() => onIncludes(true)} />
          <span><strong>Precio con IVA incluido</strong> — precio al público</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" checked={!includesTax} onChange={() => onIncludes(false)} />
          <span>Precio <strong>+ IVA</strong> — el IVA se agrega encima</span>
        </label>
      </div>
      <p className="rounded-lg bg-white px-2.5 py-1.5 text-[11px] tabular-nums text-slate-600 ring-1 ring-inset ring-slate-200">
        {includesTax
          ? <>Precio ${num2(p)} → subtotal ${num2(b.subtotal)} + IVA ${num2(b.iva)} ({taxRate}%)</>
          : <>Precio ${num2(p)} + IVA ${num2(b.iva)} ({taxRate}%) = <strong>${num2(b.total)}</strong> al público</>}
      </p>
      <L label="Costo (opcional — para margen)">
        <input className="input" type="number" min="0" step="0.01" value={cost} onChange={e => onCost(e.target.value)} placeholder="0.00" />
      </L>
      {margin != null && (
        <p className="text-[11px] text-slate-500">Margen estimado: <strong>${num2(margin)}</strong> (precio sin IVA − costo)</p>
      )}
      <p className="text-[10px] text-slate-400">En México, el precio de menú / al público normalmente ya incluye IVA. En B2B suele cotizarse “+ IVA”.</p>
    </div>
  );
}

// Set base de categorías (sirve para gastro y boutique). Se mezcla con las que el
// tenant ya tenga en su catálogo para el datalist.
const BASE_CATEGORIES = ['Comida', 'Bebida', 'Servicio', 'Producto'];

/**
 * v1.5.3 — Selector de categoría: elige una EXISTENTE del tenant o escribe una
 * NUEVA (input + datalist). La columna `category` existe desde V26; el backend la
 * acepta en create y update. Vacío → el backend aplica su default por tipo.
 */
function CategoryField({ value, categories, onChange }: {
  value: string; categories: string[]; onChange: (v: string) => void;
}) {
  const options = Array.from(new Set([...categories, ...BASE_CATEGORIES])).filter(Boolean).sort();
  return (
    <L label="Categoría">
      <input className="input" list="boutique-cat-list" value={value} maxLength={50}
        placeholder="Comida, Bebida, Servicio…" onChange={e => onChange(e.target.value)} />
      <datalist id="boutique-cat-list">{options.map(c => <option key={c} value={c} />)}</datalist>
      <p className="text-[10px] text-slate-400">Elige una existente o escribe una nueva.</p>
    </L>
  );
}

// ---------------------------------------------------------------------------
// feat/public-catalog — la categoría de PRODUCTO pasa de texto libre a ENTIDAD
// (tabla propia con nombre/color/orden). El backend expone un CRUD bajo
// /boutique/products/categories/*. Estos tipos + componentes son tenant-wide
// (NO branch-scoped): publicar/categorizar es del catálogo compartido.
// ---------------------------------------------------------------------------
type ProductCategory = { id: string; name: string; color: string | null; sortOrder: number; isActive: boolean; prepArea?: string };

const CATEGORIES_PATH = '/boutique/products/categories';
const loadCategories = () =>
  api<ProductCategory[]>(`${CATEGORIES_PATH}/list?includeInactive=true`).then((cs) => cs ?? []);

// Modal via portal a document.body (patrón de FeedbackButton): los modales nuevos
// se anclan al viewport aunque un ancestro tenga backdrop-filter. Misma estética
// que el <Modal> local (header/cuerpo scrolleable/footer sticky).
function PortalModal({
  title, onClose, children, footer, maxWidth = 'max-w-md',
}: { title: string; onClose: () => void; children: any; footer?: any; maxWidth?: string }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className={`flex max-h-[90vh] w-full ${maxWidth} flex-col overflow-hidden rounded-2xl bg-white shadow-xl`}>
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && <div className="shrink-0 border-t border-slate-100 px-6 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * feat/public-catalog — selector de categoría (entidad). Reemplaza el input de
 * texto libre en el form de producto. Envía `categoryId` (no `category`). Incluye
 * "Sin categoría" (vacío) y un alta rápida inline "+ Nueva categoría" que hace POST
 * y re-lee la lista, dejando seleccionada la nueva.
 */
function CategorySelectField({ value, categories, onChange, onCategoriesChanged }: {
  value: string | null;
  categories: ProductCategory[];
  onChange: (categoryId: string | null) => void;
  onCategoriesChanged: (cats: ProductCategory[]) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const active = categories.filter((c) => c.isActive || c.id === value);

  const create = async () => {
    const n = name.trim();
    if (!n) { setErr('Escribe un nombre.'); return; }
    setBusy(true); setErr(null);
    try {
      const created = await api<ProductCategory>(CATEGORIES_PATH, { method: 'POST', body: { name: n } });
      const fresh = await loadCategories();
      onCategoriesChanged(fresh);
      onChange(created.id);
      setName(''); setCreating(false);
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); } finally { setBusy(false); }
  };

  return (
    <L label="Categoría">
      <div className="flex items-center gap-2">
        <select className="input flex-1" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">Sin categoría</option>
          {active.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button type="button" className={btnGhost + ' shrink-0'} onClick={() => { setCreating((v) => !v); setErr(null); }}>
          {creating ? 'Cancelar' : '+ Nueva'}
        </button>
      </div>
      {creating && (
        <div className="mt-2 flex items-center gap-2">
          <input className="input flex-1" autoFocus placeholder="Nombre de la categoría" maxLength={50}
            value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); create(); } }} />
          <button type="button" className={btnPrimary + ' shrink-0'} disabled={busy} onClick={create}>
            {busy ? 'Creando…' : 'Crear'}
          </button>
        </div>
      )}
      {err && <p className="mt-1 text-[11px] text-rose-600">{err}</p>}
    </L>
  );
}

// feat/public-catalog — panel de gestión de categorías (alta/rename/baja). Borrar
// una categoría deja a sus productos SIN categoría (el backend pone categoryId=null);
// se avisa en el confirm. El caller re-lee productos + categorías al cerrar tras cambios.
function CategoryManagerModal({ initial, onClose, onChanged }: {
  initial: ProductCategory[]; onClose: () => void; onChanged: (cats: ProductCategory[]) => void;
}) {
  const { user } = useAuth();
  // V169 (🅑) — con dos estaciones, cada categoría se asigna a Cocina o Barra aquí.
  const twoStations = !!user?.tenant?.kitchenTwoStations;
  const [cats, setCats] = useState<ProductCategory[]>(initial);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  // feat/category-merge — fila de fusión in-place: id de la categoría origen y destino elegido.
  const [mergeId, setMergeId] = useState<string | null>(null);
  const [mergeInto, setMergeInto] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const refresh = async () => { const fresh = await loadCategories(); setCats(fresh); onChanged(fresh); setDirty(true); };

  const add = async () => {
    const n = newName.trim();
    if (!n) { setErr('Escribe un nombre.'); return; }
    setBusy(true); setErr(null);
    try { await api(CATEGORIES_PATH, { method: 'POST', body: { name: n } }); setNewName(''); await refresh(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); } finally { setBusy(false); }
  };
  const rename = async (id: string) => {
    const n = editName.trim();
    if (!n) { setErr('Escribe un nombre.'); return; }
    setBusy(true); setErr(null);
    try { await api(`${CATEGORIES_PATH}/${id}`, { method: 'PATCH', body: { name: n } }); setEditId(null); await refresh(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); } finally { setBusy(false); }
  };
  // V169 (🅑) — reasigna la estación (cocina|barra) de la categoría.
  const setPrep = async (id: string, area: 'cocina' | 'barra') => {
    setBusy(true); setErr(null);
    try { await api(`${CATEGORIES_PATH}/${id}`, { method: 'PATCH', body: { prepArea: area } }); await refresh(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); } finally { setBusy(false); }
  };
  const remove = async (c: ProductCategory) => {
    if (!window.confirm(`¿Eliminar la categoría "${c.name}"? Los productos que la usen quedarán SIN categoría.`)) return;
    setBusy(true); setErr(null);
    try { await api(`${CATEGORIES_PATH}/${c.id}`, { method: 'DELETE' }); await refresh(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); } finally { setBusy(false); }
  };
  // feat/category-merge — reasigna los productos de `id` a `intoId` y elimina `id`.
  const merge = async (id: string) => {
    if (!mergeInto || mergeInto === id) { setErr('Elige una categoría destino.'); return; }
    setBusy(true); setErr(null); setNote(null);
    try {
      const r = await api<{ merged: boolean; movedProducts: number; into: string }>(
        `${CATEGORIES_PATH}/${id}/merge`, { method: 'POST', body: { intoId: mergeInto } },
      );
      setMergeId(null); setMergeInto('');
      await refresh();
      setNote(`${r?.movedProducts ?? 0} productos movidos a ${r?.into ?? ''}`);
    }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); } finally { setBusy(false); }
  };

  return (
    <PortalModal
      title="Categorías"
      onClose={() => { onClose(); }}
      footer={(
        <div className="flex justify-end">
          <button type="button" className={btnPrimary} onClick={onClose}>Listo</button>
        </div>
      )}
    >
      <div className="space-y-3">
        <p className="text-[12px] text-slate-500">Organiza tu catálogo. Al eliminar una categoría, sus productos quedan sin categoría (no se borran).</p>
        <div className="flex items-center gap-2">
          <input className="input flex-1" placeholder="Nueva categoría" maxLength={50} value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <button type="button" className={btnPrimary + ' shrink-0'} disabled={busy} onClick={add}>Agregar</button>
        </div>
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {cats.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Aún no hay categorías.</p>}
          {cats.map((c) => (
            <div key={c.id} className="rounded-lg bg-slate-50 px-2.5 py-1.5">
              {editId === c.id ? (
                <div className="flex items-center gap-2">
                  <input className="input flex-1" autoFocus value={editName} maxLength={50}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); rename(c.id); } if (e.key === 'Escape') setEditId(null); }} />
                  <button type="button" className={btnPrimary + ' shrink-0'} disabled={busy} onClick={() => rename(c.id)}>Guardar</button>
                  <button type="button" className={btnGhost + ' shrink-0'} onClick={() => setEditId(null)}>Cancelar</button>
                </div>
              ) : mergeId === c.id ? (
                // feat/category-merge — elige destino (excluye la propia categoría) y confirma.
                (() => {
                  const targets = cats.filter((t) => t.id !== c.id);
                  const target = targets.find((t) => t.id === mergeInto);
                  return (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: c.color ?? '#94a3b8' }} />
                        <span className="shrink-0 text-sm text-slate-700">Fusionar «{c.name}» dentro de</span>
                        <select className="input w-full flex-1 sm:w-auto" value={mergeInto} onChange={(e) => setMergeInto(e.target.value)}>
                          <option value="">Elige categoría destino…</option>
                          {targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      {target && (
                        <p className="text-[11px] text-slate-500">
                          Fusionar «{c.name}» dentro de «{target.name}» — los productos de «{c.name}» pasarán a «{target.name}» y «{c.name}» se eliminará.
                        </p>
                      )}
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" className={btnGhost + ' shrink-0'} onClick={() => { setMergeId(null); setMergeInto(''); }}>Cancelar</button>
                        <button type="button" className={btnPrimary + ' shrink-0'} disabled={busy || !mergeInto} onClick={() => merge(c.id)}>Fusionar</button>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: c.color ?? '#94a3b8' }} />
                  <span className="flex-1 truncate text-sm text-slate-700">{c.name}{!c.isActive && <span className="ml-1.5 text-[11px] text-slate-400">(inactiva)</span>}</span>
                  {twoStations && (
                    <span className="inline-flex shrink-0 overflow-hidden rounded-md border border-slate-200 text-[11px]" title="Estación de preparación">
                      {(['cocina', 'barra'] as const).map((s) => (
                        <button key={s} type="button" disabled={busy}
                          onClick={() => setPrep(c.id, s)}
                          className={`px-2 py-0.5 capitalize transition ${(c.prepArea ?? 'cocina') === s ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'} disabled:opacity-50`}>
                          {s}
                        </button>
                      ))}
                    </span>
                  )}
                  <button type="button" className="text-[11px] font-medium text-slate-600 hover:underline" onClick={() => { setEditId(c.id); setEditName(c.name); setErr(null); }}>Renombrar</button>
                  <button type="button" className="text-[11px] font-medium text-indigo-600 hover:underline disabled:opacity-50" disabled={busy || cats.length < 2} onClick={() => { setMergeId(c.id); setMergeInto(''); setEditId(null); setErr(null); setNote(null); }}>Fusionar</button>
                  <button type="button" className="text-[11px] font-medium text-rose-600 hover:underline disabled:opacity-50" disabled={busy} onClick={() => remove(c)}>Eliminar</button>
                </div>
              )}
            </div>
          ))}
        </div>
        {note && <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-700 ring-1 ring-inset ring-emerald-600/15">{note}</p>}
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
      </div>
    </PortalModal>
  );
}

// feat/public-catalog — pill de estado de publicación (tienda pública).
function PublicPill({ isPublic }: { isPublic: boolean }) {
  return isPublic
    ? <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/15">Público</span>
    : <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 ring-1 ring-inset ring-slate-200">Privado</span>;
}

// ---------------------------------------------------------------------------
// feat/products-filter — filtro/búsqueda client-side para las listas de catálogo.
// El catálogo se carga completo en memoria (GET /boutique/{products,services} sin
// paginación server-side), así que el filtro es local, sin llamadas nuevas.
// ---------------------------------------------------------------------------
/** Normaliza para comparar: minúsculas y sin acentos ("Café" ≈ "cafe"). */
const norm = (s: any) =>
  String(s ?? '').toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');

/** Barra compartida por Productos/Servicios: búsqueda + categoría + inactivos. */
function CatalogFilterBar({ q, onQ, cat, onCat, categories, showInactive, onShowInactive, placeholder, count, total }: {
  q: string; onQ: (v: string) => void;
  cat: string; onCat: (v: string) => void; categories: string[];
  showInactive: boolean; onShowInactive: (v: boolean) => void;
  placeholder: string; count: number; total: number;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <div className="w-full sm:w-64"><input className="input" placeholder={placeholder} value={q} onChange={(e) => onQ(e.target.value)} /></div>
      <div className="w-48">
        <select className="input" value={cat} onChange={(e) => onCat(e.target.value)}>
          <option value="">Todas las categorías</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-1.5 text-xs text-slate-600">
        <input type="checkbox" checked={showInactive} onChange={(e) => onShowInactive(e.target.checked)} />
        Mostrar inactivos
      </label>
      {count !== total && <span className="text-xs tabular-nums text-slate-400">{count} de {total}</span>}
    </div>
  );
}

/** Mensaje de estado vacío cuando el filtro no matchea (el catálogo sí tiene items). */
function noResultsMsg(q: string) {
  const t = q.trim();
  return t ? <>Sin resultados para «{t}».</> : <>Sin resultados con los filtros aplicados.</>;
}

// ---------------------------------------------------------------------------
// "Contactar cliente" — abre WhatsApp (wa.me) y/o email (mailto) con los datos
// del contacto que YA viajan en la fila (LEFT JOIN a contacts). Es el mecanismo
// de recordatorio manual que pidió producto; sin backend de envío.
// ---------------------------------------------------------------------------
function waHref(phone?: string | null): string | null {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, '');
  // Se requiere al menos un número marcable; wa.me espera el número con lada país.
  return digits.length >= 8 ? `https://wa.me/${digits}` : null;
}

/** Gmail compose en el navegador — útil para quien no tiene cliente mailto:
 *  configurado (la mayoría en desktop). Complementa, no reemplaza, al mailto. */
function gmailHref(email: string, subject: string, body: string): string {
  const p = new URLSearchParams({ view: 'cm', fs: '1', to: email, su: subject, body });
  return `https://mail.google.com/mail/?${p.toString()}`;
}

export function ContactActions({ name, phone, email, message: messageOverride, subject: subjectOverride }: {
  name?: string | null; phone?: string | null; email?: string | null;
  // v1.4.6 — el caller (Recordatorios) puede pasar el texto del propio recordatorio.
  message?: string; subject?: string;
}) {
  const wa = waHref(phone);
  const firstName = name ? name.trim().split(/\s+/)[0] : null;
  const greeting = firstName ? `Hola ${firstName}, ` : 'Hola, ';
  const message = messageOverride?.trim()
    ? `${greeting}${messageOverride.trim()} `
    : `${greeting}te escribimos para dar seguimiento a tu servicio. `;
  const waText = encodeURIComponent(message);
  const subject = subjectOverride?.trim() || 'Seguimiento de tu servicio';
  const mailSubject = encodeURIComponent(subject);
  if (!wa && !email) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {wa && (
        <a
          href={`${wa}?text=${waText}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Contactar por WhatsApp"
          className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-700/15 hover:bg-emerald-100"
        >
          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
        </a>
      )}
      {email && (
        <a
          href={`mailto:${email}?subject=${mailSubject}`}
          title="Email con tu cliente de correo predeterminado"
          className="inline-flex items-center gap-1 rounded-lg bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-100"
        >
          <Mail className="h-3.5 w-3.5" /> Email
        </a>
      )}
      {email && (
        <a
          href={gmailHref(email, subject, message)}
          target="_blank"
          rel="noopener noreferrer"
          title="Redactar en Gmail (navegador)"
          className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 ring-1 ring-inset ring-rose-700/15 hover:bg-rose-100"
        >
          <Mail className="h-3.5 w-3.5" /> Gmail
        </a>
      )}
    </div>
  );
}

// ===========================================================================
// Servicios
// ===========================================================================
export function ServicesView() {
  const { user } = useAuth();
  const canWrite = can(user, 'service_catalog:write');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<any | null>(null);

  const load = async () => {
    setLoading(true);
    try { setRows((await api('/boutique/services?includeInactive=true')) as any[]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const categories = useMemo(() => Array.from(new Set(rows.map((r) => r.category).filter(Boolean))) as string[], [rows]);

  // feat/products-filter — búsqueda + categoría + activos/inactivos, client-side.
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const filtered = useMemo(() => {
    const needle = norm(q.trim());
    return rows.filter((s) =>
      (showInactive || s.isActive) &&
      (!cat || (s.category ?? '') === cat) &&
      (!needle || norm(s.name).includes(needle)));
  }, [rows, q, cat, showInactive]);

  return (
    <div>
      <Header title="Servicios" subtitle="Catálogo de servicios que ofreces."
        action={canWrite ? <button className={btnPrimary} onClick={() => setOpen(true)}>+ Nuevo servicio</button> : undefined} />
      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : (<>
        <CatalogFilterBar q={q} onQ={setQ} cat={cat} onCat={setCat} categories={categories}
          showInactive={showInactive} onShowInactive={setShowInactive}
          placeholder="Buscar por nombre…" count={filtered.length} total={rows.length} />
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-2">Servicio</th><th className="px-4 py-2">Categoría</th><th className="px-4 py-2">Precio</th><th className="px-4 py-2">Duración</th><th className="px-4 py-2">Estado</th><th className="px-4 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">Sin servicios todavía.</td></tr>}
              {rows.length > 0 && filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">{noResultsMsg(q)}</td></tr>}
              {filtered.map((s) => (
                <tr key={s.id} className={s.isActive ? '' : 'opacity-50'}>
                  <td className="px-4 py-2"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: s.color ?? '#94a3b8' }} /> <span className="font-medium text-slate-800">{s.name}</span></td>
                  <td className="px-4 py-2">{s.category ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{s.category}</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-2 tabular-nums">{money(s.defaultPrice, s.currency)}</td>
                  <td className="px-4 py-2">{s.durationMinutes} min</td>
                  <td className="px-4 py-2">{s.isActive ? 'Activo' : 'Inactivo'}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    {canWrite ? (<>
                      <button className={btnGhost} onClick={() => setEdit(s)}>Editar</button>
                      {s.isActive && <button className={btnGhost} onClick={async () => { await api(`/boutique/services/${s.id}`, { method: 'DELETE' }); load(); }}>Desactivar</button>}
                    </>) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}
      {open && <ServiceModal categories={categories} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {edit && <ServiceModal item={edit} categories={categories} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function ServiceModal({ item, categories, onClose, onSaved }: { item?: any; categories: string[]; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!item;
  const taxRate = Number(item?.taxRate ?? 16);
  const [f, setF] = useState({
    name: item?.name ?? '', defaultPrice: String(item?.defaultPrice ?? '0'), currency: item?.currency ?? 'MXN',
    durationMinutes: String(item?.durationMinutes ?? '60'), color: item?.color ?? '#94a3b8',
    description: item?.description ?? '', cost: item?.cost != null ? String(item.cost) : '',
    priceIncludesTax: item?.priceIncludesTax ?? true, category: item?.category ?? '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: any) => {
    e.preventDefault(); setErr(null); setBusy(true);
    const body = {
      name: f.name, defaultPrice: Number(f.defaultPrice), currency: f.currency,
      durationMinutes: Number(f.durationMinutes), color: f.color, description: f.description || undefined,
      cost: f.cost ? Number(f.cost) : undefined, priceIncludesTax: f.priceIncludesTax, taxRate,
      category: f.category.trim() || undefined,
    };
    try {
      if (isEdit) await api(`/boutique/services/${item.id}`, { method: 'PATCH', body });
      else await api('/boutique/services', { method: 'POST', body });
      onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); } finally { setBusy(false); }
  };
  return (
    <Modal
      title={isEdit ? 'Editar servicio' : 'Nuevo servicio'}
      onClose={onClose}
      footer={(
        <div className="flex justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
          <button type="submit" form="service-form" className={btnPrimary} disabled={busy}>{busy ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear'}</button>
        </div>
      )}
    >
      <form id="service-form" onSubmit={submit} className="space-y-3">
        <L label="Nombre"><input className="input" required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></L>
        <div className="grid grid-cols-2 gap-3">
          <L label="Precio"><input className="input" type="number" min="0" step="0.01" value={f.defaultPrice} onChange={e => setF({ ...f, defaultPrice: e.target.value })} /></L>
          <L label="Moneda"><input className="input" maxLength={3} value={f.currency} onChange={e => setF({ ...f, currency: e.target.value.toUpperCase() })} /></L>
        </div>
        <CategoryField value={f.category} categories={categories} onChange={v => setF({ ...f, category: v })} />
        <IvaFields price={f.defaultPrice} cost={f.cost} includesTax={f.priceIncludesTax} taxRate={taxRate}
          onCost={v => setF({ ...f, cost: v })} onIncludes={v => setF({ ...f, priceIncludesTax: v })} />
        <div className="grid grid-cols-2 gap-3">
          <L label="Duración (min)"><input className="input" type="number" min="0" value={f.durationMinutes} onChange={e => setF({ ...f, durationMinutes: e.target.value })} /></L>
          <L label="Color"><input className="input" type="color" value={f.color} onChange={e => setF({ ...f, color: e.target.value })} /></L>
        </div>
        <L label="Descripción"><textarea className="input" rows={2} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></L>
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
      </form>
    </Modal>
  );
}

// ===========================================================================
// Productos
// ===========================================================================
export function ProductsView() {
  const { user } = useAuth();
  const canWrite = can(user, 'product_catalog:write');
  const canAdjustStock = can(user, 'product_catalog:adjust_stock');
  // V54 — la receta de insumos es un feature de A&B: el boton "Receta" aparece solo si
  // el tenant tiene el modulo gastro y el usuario puede gestionar catalogo (write).
  const hasGastro = ((user?.tenant?.enabledModules ?? []) as string[]).includes('gastro');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [adjust, setAdjust] = useState<any | null>(null);
  const [adjustDelta, setAdjustDelta] = useState(0); // P2 — prellenado del modal (edición negativa inline)
  const [adjustVariant, setAdjustVariant] = useState<any | null>(null); // PR-3 — ajuste por talla
  const [repartoFor, setRepartoFor] = useState<any | null>(null); // T3 — reparto guiado por talla
  const [edit, setEdit] = useState<any | null>(null);
  const [recipeFor, setRecipeFor] = useState<any | null>(null);
  const [bulk, setBulk] = useState(false);
  // feat/public-catalog — categorías (entidad) + modo selección para publicar en lote.
  // OJO: publicar es TENANT-WIDE (no depende de la sucursal activa), a diferencia del
  // ajuste de stock en lote. No hay branchId en ningún punto del flujo de publicación.
  const [prodCategories, setProdCategories] = useState<ProductCategory[]>([]);
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [publishBusy, setPublishBusy] = useState(false);
  // V106 — precio por sucursal en contexto del producto (>1 sucursal).
  const multiBranch = useHasMultipleBranches();
  // feat/branch-write-require-active — toda escritura de stock exige branchId explícito
  // (backend 400 si falta). Destino = sucursal activa de la sesión; en consolidado con
  // >1 sucursal (needsChoice) no hay destino único → se deshabilitan los disparadores.
  const { writeBranchId, needsChoice } = useActiveWriteBranch();
  const [pricingFor, setPricingFor] = useState<any | null>(null);

  // V87 — riesgo de inventario (umbral + predicción): misma lista del digest.
  // Best-effort: si el endpoint falla (permiso/módulo) la vista sigue normal.
  const [risk, setRisk] = useState<any | null>(null);
  const [alertCfgOpen, setAlertCfgOpen] = useState(false);
  const load = async () => {
    setLoading(true);
    try { setRows((await api('/boutique/products?includeInactive=true')) as any[]); }
    finally { setLoading(false); }
    api('/inventory/risk').then(setRisk).catch(() => setRisk(null));
    loadCategories().then(setProdCategories).catch(() => setProdCategories([]));
  };
  useEffect(() => { load(); }, []);
  // feat/public-catalog — nombre de categoría por id, para pintar el pill en la fila
  // (la lista de productos trae categoryId, no el nombre).
  const catNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of prodCategories) m.set(c.id, c.name);
    return m;
  }, [prodCategories]);
  const riskByProduct = useMemo(() => {
    const m = new Map<string, any>();
    for (const p of risk?.products ?? []) m.set(p.productId, p);
    return m;
  }, [risk]);
  const categories = useMemo(() => Array.from(new Set(rows.map((r) => r.category).filter(Boolean))) as string[], [rows]);
  const tracked = useMemo(() => rows.filter((p) => p.trackInventory && p.isActive), [rows]);

  // feat/products-filter — búsqueda (nombre, variante, SKU) + categoría +
  // activos/inactivos, client-side. Si solo matchea una variante, el producto
  // igual aparece (la fila lista productos; las variantes viajan en p.variants).
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const needle = norm(q.trim());
  const filtered = useMemo(() => rows.filter((p) =>
    (showInactive || p.isActive) &&
    (!cat || (p.category ?? '') === cat) &&
    (!needle || norm(p.name).includes(needle) || norm(p.sku).includes(needle) ||
      (p.variants ?? []).some((v: any) => norm(v.name).includes(needle)))),
  [rows, needle, cat, showInactive]);

  // feat/public-catalog — publicar/quitar un producto individual (tienda pública).
  // TENANT-WIDE: sin branchId. Actualiza la fila en memoria para respuesta inmediata.
  const publishOne = async (p: any, isPublic: boolean) => {
    setRows((rs) => rs.map((r) => (r.id === p.id ? { ...r, isPublic } : r)));
    try { await api(`/boutique/products/${p.id}/publish`, { method: 'POST', body: { isPublic } }); }
    catch (e: any) { window.alert(e?.body?.message || e?.message || 'No se pudo cambiar la publicación'); load(); }
  };
  // feat/public-catalog — publicación en lote (multi-select). NO usa branchId.
  const bulkPublish = async (isPublic: boolean) => {
    const productIds = [...selected];
    if (productIds.length === 0) return;
    setPublishBusy(true);
    try {
      await api('/boutique/products/bulk-publish', { method: 'POST', body: { productIds, isPublic } });
      setSelected(new Set()); setSelectMode(false); load();
    } catch (e: any) { window.alert(e?.body?.message || e?.message || 'No se pudo publicar en lote'); }
    finally { setPublishBusy(false); }
  };
  const toggleSel = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));
  const toggleSelAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allFilteredSelected) filtered.forEach((p) => n.delete(p.id));
    else filtered.forEach((p) => n.add(p.id));
    return n;
  });

  return (
    <div>
      <Header title="Productos" subtitle="Catálogo de productos con inventario básico."
        action={(canWrite || canAdjustStock) ? (
          <div className="flex flex-wrap items-center gap-2">
            {/* feat/public-catalog — gestión de categorías (entidad) + modo selección para
                publicar en lote. Publicar es tenant-wide: NO se gatea por sucursal. */}
            {canWrite && <button className={btnGhost} onClick={() => setCatMgrOpen(true)}>Categorías</button>}
            {canWrite && rows.length > 0 && (
              <button className={btnGhost} onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }}>
                {selectMode ? 'Salir de selección' : 'Publicar en tienda'}
              </button>
            )}
            {/* feat/branch-write-require-active — el ajuste en lote escribe stock: exige
                sucursal destino. En consolidado con >1 sucursal (needsChoice) no hay
                destino único → botón deshabilitado + pista visible a la barra superior. */}
            {canAdjustStock && tracked.length > 0 && needsChoice && (
              <span className="text-[11px] text-slate-400">Elige una sucursal en la barra superior para ajustar</span>
            )}
            {canAdjustStock && tracked.length > 0 && (
              <button className={btnGhost} disabled={needsChoice} onClick={() => setBulk(true)}
                title={needsChoice ? 'Elige una sucursal concreta en la barra superior para ajustar; en vista consolidada no hay destino único.' : undefined}>
                Ajuste en lote
              </button>
            )}
            {canWrite && <button className={btnPrimary} onClick={() => setOpen(true)}>+ Nuevo producto</button>}
          </div>
        ) : undefined} />
      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : (<>
        {/* V87 — banner de riesgo: la misma lista que llega en el digest diario. */}
        {risk && (risk.products?.length > 0) && (
          <div className="mb-3 flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span>
              ⚠ {risk.products.length} producto{risk.products.length === 1 ? '' : 's'} en riesgo
              {risk.products.some((p: any) => p.reason === 'PREDICTED') &&
                ' (incluye predicción: se agotan antes de tu tiempo de reabasto)'}
              . El admin recibe el resumen diario por correo.
            </span>
            {canWrite && (
              <button className="ml-3 shrink-0 rounded-lg px-2 py-1 font-medium ring-1 ring-amber-300 hover:bg-amber-100"
                onClick={() => setAlertCfgOpen(true)}>Ajustes de alertas</button>
            )}
          </div>
        )}
        <CatalogFilterBar q={q} onQ={setQ} cat={cat} onCat={setCat} categories={categories}
          showInactive={showInactive} onShowInactive={setShowInactive}
          placeholder="Buscar por nombre, variante o SKU…" count={filtered.length} total={rows.length} />
        {/* feat/public-catalog — barra de acciones en lote (modo selección). Publicar es
            TENANT-WIDE: sin sucursal. Se muestra solo con selección activa. */}
        {selectMode && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <span className="text-xs text-slate-600">{selected.size} seleccionado{selected.size === 1 ? '' : 's'}</span>
            <button className={btnGhost} onClick={toggleSelAll} disabled={filtered.length === 0}>
              {allFilteredSelected ? 'Quitar selección' : 'Seleccionar todos'}
            </button>
            <div className="ml-auto flex flex-wrap gap-2">
              <button className={btnPrimary} disabled={publishBusy || selected.size === 0} onClick={() => bulkPublish(true)}>
                {publishBusy ? 'Aplicando…' : 'Publicar seleccionados'}
              </button>
              <button className={btnGhost} disabled={publishBusy || selected.size === 0} onClick={() => bulkPublish(false)}>
                Quitar de la tienda
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                {selectMode && <th className="px-4 py-2"><input type="checkbox" checked={allFilteredSelected} onChange={toggleSelAll} title="Seleccionar todos" /></th>}
                <th className="px-4 py-2">Producto</th><th className="px-4 py-2">Categoría</th><th className="px-4 py-2">SKU</th><th className="px-4 py-2">Precio</th><th className="px-4 py-2">Stock</th><th className="px-4 py-2">Tienda</th><th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td colSpan={selectMode ? 8 : 7} className="px-4 py-6 text-center text-slate-400">Sin productos todavía.</td></tr>}
              {rows.length > 0 && filtered.length === 0 && <tr><td colSpan={selectMode ? 8 : 7} className="px-4 py-6 text-center text-slate-400">{noResultsMsg(q)}</td></tr>}
              {filtered.map((p) => (
                <tr key={p.id} className={p.isActive ? '' : 'opacity-50'}>
                  {selectMode && (
                    <td className="px-4 py-2">
                      <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} />
                    </td>
                  )}
                  <td className="px-4 py-2 font-medium text-slate-800">
                    {p.name}
                    {/* Si la fila aparece porque matcheó una variante (no el nombre/SKU), se indica cuál. */}
                    {needle !== '' && !norm(p.name).includes(needle) && !norm(p.sku).includes(needle) && (
                      <span className="ml-1.5 text-[11px] font-normal text-slate-400">
                        variante: {(p.variants ?? []).filter((v: any) => norm(v.name).includes(needle)).map((v: any) => v.name).join(', ')}
                      </span>
                    )}
                    {/* V87 — badge de predicción ("se agota en ~X días") y agotado. El
                        "ya bajo umbral" lo marca el ⚠ del stock (comportamiento previo). */}
                    {(() => {
                      const r = riskByProduct.get(p.id);
                      if (!r) return null;
                      if (r.reason === 'OUT') return <span className="ml-1.5 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700">agotado</span>;
                      if (r.reason === 'PREDICTED') return <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">se agota en ~{Math.ceil(r.daysToStockout)} d</span>;
                      return null;
                    })()}
                  </td>
                  {/* feat/public-catalog — la categoría ahora es entidad (categoryId);
                      se muestra su nombre. Se cae al viejo texto `category` si aún viniera. */}
                  <td className="px-4 py-2">{(() => {
                    const label = (p.categoryId && catNameById.get(p.categoryId)) || p.category;
                    return label ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{label}</span> : <span className="text-slate-300">—</span>;
                  })()}</td>
                  <td className="px-4 py-2 text-slate-500">{p.sku ?? '—'}</td>
                  <td className="px-4 py-2 tabular-nums">{money(p.basePrice, p.currency)}</td>
                  <td className="px-4 py-2 tabular-nums">{p.trackInventory
                    ? ((p.variants?.length ?? 0) > 0
                        ? <VariantStock product={p} canAdjust={canAdjustStock} canReparto={canWrite} writeBranchId={writeBranchId} needsChoice={needsChoice} onAdjust={(v: any) => { setAdjustVariant(v); setAdjustDelta(0); setAdjust(p); }} onReparto={() => setRepartoFor(p)} />
                        : <InlineStock product={p} canAdjust={canAdjustStock} writeBranchId={writeBranchId} needsChoice={needsChoice} onSaved={load} onRequestModal={(d) => { setAdjustVariant(null); setAdjustDelta(d); setAdjust(p); }} />)
                    : (p.hasRecipe ? <InsumoStockHint product={p} /> : '—')}</td>
                  {/* feat/public-catalog — estado + toggle de publicación por producto (tenant-wide). */}
                  <td className="px-4 py-2">
                    {canWrite ? (
                      <button type="button" onClick={() => publishOne(p, !p.isPublic)}
                        title={p.isPublic ? 'Quitar de la tienda pública' : 'Publicar en la tienda pública'}
                        className="cursor-pointer">
                        <PublicPill isPublic={!!p.isPublic} />
                      </button>
                    ) : <PublicPill isPublic={!!p.isPublic} />}
                  </td>
                  <td className="px-4 py-2 text-right space-x-2">
                    {(canWrite || canAdjustStock) ? (<>
                      {canWrite && hasGastro && <button className={btnGhost} onClick={() => setRecipeFor(p)}>Receta</button>}
                      {canWrite && <button className={btnGhost} onClick={() => setEdit(p)}>Editar</button>}
                      {canWrite && multiBranch && <button className={btnGhost} onClick={() => setPricingFor(p)}>$ sucursal</button>}
                      {canAdjustStock && p.trackInventory && (
                        <button className={btnGhost} disabled={needsChoice} onClick={() => { setAdjustVariant(null); setAdjustDelta(0); setAdjust(p); }}
                          title={needsChoice ? 'Elige una sucursal concreta en la barra superior para ajustar; en vista consolidada no hay destino único.' : undefined}>Ajustar stock</button>
                      )}
                      {canWrite && p.isActive && <button className={btnGhost} onClick={async () => { await api(`/boutique/products/${p.id}`, { method: 'DELETE' }); load(); }}>Desactivar</button>}
                    </>) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* V106/V107 — herramientas por sucursal, en la vista de Productos (se
            auto-ocultan con 1 sola sucursal: coexistencia). */}
        <BranchTransfers />
        <BranchPricing />
      </>)}
      {pricingFor && <ProductBranchPriceModal productId={pricingFor.id} productName={pricingFor.name} basePrice={pricingFor.basePrice} onClose={() => setPricingFor(null)} />}
      {open && <ProductModal categories={categories} prodCategories={prodCategories} onCategoriesChanged={setProdCategories} hasGastro={hasGastro} writeBranchId={writeBranchId} needsChoice={needsChoice} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {edit && <ProductModal item={edit} categories={categories} prodCategories={prodCategories} onCategoriesChanged={setProdCategories} hasGastro={hasGastro} writeBranchId={writeBranchId} needsChoice={needsChoice} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {adjust && <AdjustStockModal product={adjust} variant={adjustVariant} initialDelta={adjustDelta} writeBranchId={writeBranchId} onClose={() => { setAdjust(null); setAdjustVariant(null); }} onSaved={() => { setAdjust(null); setAdjustVariant(null); load(); }} />}
      {recipeFor && <RecipeModal product={recipeFor} onClose={() => setRecipeFor(null)} />}
      {repartoFor && <StockRepartoModal productId={repartoFor.id} focusBranchId={writeBranchId} onClose={() => setRepartoFor(null)} onSaved={() => { setRepartoFor(null); load(); }} />}
      {bulk && writeBranchId && <BulkAdjustStockModal products={tracked} writeBranchId={writeBranchId} onClose={() => setBulk(false)} onSaved={() => { setBulk(false); load(); }} />}
      {alertCfgOpen && <AlertSettingsModal onClose={() => setAlertCfgOpen(false)} onSaved={() => { setAlertCfgOpen(false); load(); }} />}
      {/* feat/public-catalog — gestión de categorías (entidad). Al cerrar tras cambios,
          se re-lee productos + categorías para reflejar renombres/bajas. */}
      {catMgrOpen && (
        <CategoryManagerModal
          initial={prodCategories}
          onChanged={setProdCategories}
          onClose={() => { setCatMgrOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// V87 — ajustes de alertas de inventario del tenant (digest diario + predicción).
// Sin fila previa = defaults (14 días de ventana, 7 de reabasto, activado).
function AlertSettingsModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState<{ enabled: boolean; velocityWindowDays: string; defaultLeadTimeDays: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api('/inventory/alert-settings')
      .then((s: any) => setF({
        enabled: !!s.enabled,
        velocityWindowDays: String(s.velocityWindowDays ?? 14),
        defaultLeadTimeDays: String(s.defaultLeadTimeDays ?? 7),
      }))
      .catch((e: any) => setErr(e?.body?.message || e?.message || 'No se pudo cargar'));
  }, []);

  const submit = async (e: any) => {
    e.preventDefault();
    if (!f) return;
    setBusy(true); setErr(null);
    try {
      await api('/inventory/alert-settings', {
        method: 'PUT',
        body: {
          enabled: f.enabled,
          velocityWindowDays: Number(f.velocityWindowDays) || 14,
          defaultLeadTimeDays: Number(f.defaultLeadTimeDays) || 7,
        },
      });
      onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); setBusy(false); }
  };

  return (
    <Modal title="Alertas de inventario" onClose={onClose}>
      {!f ? <p className="text-sm text-slate-500">{err ?? 'Cargando…'}</p> : (
        <form onSubmit={submit} className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} />
            Enviar resumen diario por correo cuando haya productos en riesgo
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L label="Días de ventas a considerar (7–60)">
                <input className="input" type="number" min="7" max="60" value={f.velocityWindowDays}
                  onChange={(e) => setF({ ...f, velocityWindowDays: e.target.value })} />
              </L>
              <p className="mt-1 text-[11px] leading-snug text-slate-400">
                Cuántos días recientes usamos para calcular qué tan rápido se vende cada producto.
              </p>
            </div>
            <div>
              <L label="Tiempo de reabasto (días, 1–60)">
                <input className="input" type="number" min="1" max="60" value={f.defaultLeadTimeDays}
                  onChange={(e) => setF({ ...f, defaultLeadTimeDays: e.target.value })} />
              </L>
              <p className="mt-1 text-[11px] leading-snug text-slate-400">
                Cuántos días tarda en llegar un producto cuando lo pides a tu proveedor.
                Lo usamos para avisarte antes de que se agote.
              </p>
            </div>
          </div>
          <p className="rounded-xl bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
            Se avisa cuando un producto está por debajo de su mínimo O cuando, a su ritmo de
            venta, se agotaría antes de su tiempo de reabasto. Cada producto puede tener su
            propio "días de reabasto" en su editor.
          </p>
          {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
            <button className={btnPrimary} disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// feat/inventory-management — edición ágil de stock en la tabla: ±1 rápidos + clic
// en el número para fijar un valor exacto (calcula el delta). Cada cambio registra
// un movimiento de inventario (con el usuario) vía /adjust-stock.
function InlineStock({ product, onSaved, canAdjust = true, writeBranchId = null, needsChoice = false, onRequestModal }: { product: any; onSaved: () => void; canAdjust?: boolean; writeBranchId?: string | null; needsChoice?: boolean; onRequestModal?: (delta: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(product.currentStock));
  const [busy, setBusy] = useState(false);
  // P1 — una escritura por acción: la llave se genera al ENTRAR a editar y se reusa;
  // el guard evita el doble commit (onKeyDown Enter + onBlur al desmontar el input).
  const keyRef = useRef<string>('');
  const committedRef = useRef(false);
  const low = product.lowStockAlertAt != null && product.currentStock <= product.lowStockAlertAt;

  // Cajero/Contador: stock de solo-lectura (sin ±1 ni edición directa). El backend
  // ya 403ea /adjust-stock sin product_catalog:adjust_stock; aquí ocultamos los controles.
  // feat/branch-write-require-active — en consolidado con >1 sucursal (needsChoice) no
  // hay destino único para escribir → mostramos el stock de solo-lectura (los ±/edición
  // exigirían branchId que no tenemos); la pista para elegir sucursal está en el header.
  if (!canAdjust || needsChoice) {
    return (
      <span className={`min-w-[2rem] tabular-nums ${low ? 'font-semibold text-rose-600' : 'text-slate-700'}`}
        title={needsChoice ? 'Elige una sucursal concreta en la barra superior para ajustar el stock.' : undefined}>
        {product.currentStock}{low ? ' ⚠' : ''}
      </span>
    );
  }

  const apply = async (delta: number, reason: string) => {
    if (delta === 0) { setEditing(false); return; }
    if (!keyRef.current) keyRef.current = crypto.randomUUID();
    const adjustmentKey = keyRef.current;
    setBusy(true);
    try { await api(`/boutique/products/${product.id}/adjust-stock`, { method: 'POST', body: { delta, reason, type: 'ajuste', branchId: writeBranchId, adjustmentKey } }); onSaved(); }
    catch (e: any) { window.alert(e?.body?.message || e?.message || 'No se pudo ajustar el stock'); }
    finally { setBusy(false); setEditing(false); }
  };
  const commitEdit = () => {
    if (committedRef.current) return;         // ya se confirmó esta edición (evita el 2º disparo)
    const next = Number(val);
    if (!Number.isFinite(next)) { setEditing(false); return; }
    const delta = next - product.currentStock;
    committedRef.current = true;
    // P2 — restar por edición directa exige un motivo: se abre el modal "Ajustar stock"
    // YA PRELLENADO con la reducción (el usuario solo agrega el motivo).
    if (delta < 0 && onRequestModal) { setEditing(false); onRequestModal(delta); return; }
    apply(delta, 'Edición directa');
  };

  if (editing) {
    return (
      <input autoFocus type="number" disabled={busy}
        className="w-16 rounded-lg border border-slate-300 px-2 py-1 text-sm tabular-nums"
        value={val} onChange={(e) => setVal(e.target.value)} onBlur={commitEdit}
        onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') { committedRef.current = true; setEditing(false); } }} />
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <button title="Quitar 1" disabled={busy} onClick={() => { keyRef.current = crypto.randomUUID(); apply(-1, 'Ajuste rápido -1'); }}
        className="h-6 w-6 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">−</button>
      <button title="Editar stock" onClick={() => { setVal(String(product.currentStock)); keyRef.current = crypto.randomUUID(); committedRef.current = false; setEditing(true); }}
        className={`min-w-[2rem] tabular-nums ${low ? 'font-semibold text-rose-600' : 'text-slate-700'}`}>
        {product.currentStock}{low ? ' ⚠' : ''}
      </button>
      <button title="Agregar 1" disabled={busy} onClick={() => { keyRef.current = crypto.randomUUID(); apply(1, 'Ajuste rápido +1'); }}
        className="h-6 w-6 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">+</button>
    </span>
  );
}

// PR-3 (V189) — inventario POR TALLA: cada variante muestra su saldo de la sucursal
// activa (onHand del overlay). El ajuste product-level ya no aplica (el backend exige
// variante); aquí el lápiz abre el modal de ajuste PREFIJADO con esa talla.
// T3 (V218) — en una sucursal donde el producto sigue en POOL (sin repartir) se muestra la
// existencia compartida y "Repartir por talla"; no hay lápiz por talla (el backend lo
// bloquea). En un desglosado con existencia SIN TALLA (varada) se avisa y se ofrece el
// reparto; una talla negativa sale marcada para corregirla contando.
export function VariantStock({ product, canAdjust, canReparto = false, writeBranchId, needsChoice, onAdjust, onReparto }: {
  product: any; canAdjust: boolean; canReparto?: boolean; writeBranchId: string | null; needsChoice: boolean;
  onAdjust: (v: any) => void; onReparto?: () => void;
}) {
  const variants: any[] = product.variants ?? [];
  const editable = canAdjust && !needsChoice && !!writeBranchId;
  const pool = product.stockMode === 'pool';
  const poolOnHand = Number(product.poolOnHand ?? 0);
  const repartoBtn = canReparto && onReparto && !needsChoice && (
    <button type="button" onClick={onReparto} className="text-[11px] font-medium text-[var(--brand-primary)] hover:underline">Repartir por talla</button>
  );
  if (pool && !needsChoice) {
    return (
      <div className="space-y-0.5">
        <div className="text-xs text-slate-700">
          <span className={`tabular-nums font-semibold ${poolOnHand <= 0 ? 'text-rose-600' : ''}`}>{poolOnHand}</span>
          <span className="ml-1 text-slate-500">sin repartir · {variants.map((v) => v.name).join(' / ')}</span>
        </div>
        {repartoBtn}
      </div>
    );
  }
  return (
    <div className="space-y-0.5" title={needsChoice ? 'Elige una sucursal concreta en la barra superior para ver/ajustar por talla.' : undefined}>
      {!needsChoice && poolOnHand !== 0 && (
        <div className="text-[11px] font-medium text-amber-700">
          {poolOnHand < 0 ? `${-poolOnHand} venta(s) sin talla: asígnala(s) en el reparto` : `${poolOnHand} sin talla (no se ven)`} {repartoBtn}
        </div>
      )}
      {variants.map((v) => {
        const oh = v.onHand ?? null; // null = sin sucursal resuelta (consolidado)
        const out = oh != null && oh <= 0;
        const negative = oh != null && oh < 0;
        return (
          <div key={v.id} className="flex items-center gap-1.5 text-xs">
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">{v.name}</span>
            <span className={`tabular-nums ${out ? 'font-semibold text-rose-600' : 'text-slate-700'}`}>
              {oh != null ? oh : '—'}{negative ? ' · negativa: repártela contando' : out ? ' · agotada' : ''}
            </span>
            {editable && (
              <button type="button" title={`Ajustar talla ${v.name}`} onClick={() => onAdjust({ id: v.id, name: v.name, onHand: oh ?? 0 })}
                className="text-slate-400 hover:text-slate-700">✎</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// P3 (fix) — producto CON receta: no tiene stock propio; su disponibilidad = el insumo
// mas escaso (availableQty, BOM). Avisa cuando un insumo se agoto/esta por agotarse y
// manda a la seccion Insumos (sin desglosar cual insumo — no siempre se sabe la receta).
function InsumoStockHint({ product }: { product: any }) {
  const avail = product.availableQty;
  if (avail == null) return <span className="text-slate-500" title="Se controla por receta (insumos)">por receta</span>;
  // Rotulado "por receta: N" (SOLO LECTURA): el stock se corrige en Insumos, no aqui.
  const label = <span className="tabular-nums text-slate-600" title="Disponibilidad calculada por su receta (insumos). Se ajusta en la sección Insumos.">por receta: {avail}</span>;
  if (avail <= 0) {
    return (
      <span className="inline-flex flex-col">
        {label}
        <span className="text-[11px] font-medium text-rose-600">Insumo agotado — revisa <b>Insumos</b></span>
      </span>
    );
  }
  if (avail <= 3) {
    return (
      <span className="inline-flex flex-col">
        {label}
        <span className="text-[11px] font-medium text-amber-600">Insumo por agotarse — revisa <b>Insumos</b></span>
      </span>
    );
  }
  return label;
}

function ProductModal({ item, categories, prodCategories, onCategoriesChanged, hasGastro, writeBranchId = null, needsChoice = false, onClose, onSaved }: { item?: any; categories: string[]; prodCategories: ProductCategory[]; onCategoriesChanged: (cats: ProductCategory[]) => void; hasGastro?: boolean; writeBranchId?: string | null; needsChoice?: boolean; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!item;
  const taxRate = Number(item?.taxRate ?? 16);
  const [f, setF] = useState({
    name: item?.name ?? '', sku: item?.sku ?? '', basePrice: String(item?.basePrice ?? '0'),
    currency: item?.currency ?? 'MXN', currentStock: String(item?.currentStock ?? '0'),
    trackInventory: item?.trackInventory ?? false,
    lowStockAlertAt: item?.lowStockAlertAt != null ? String(item.lowStockAlertAt) : '',
    // V87 — días de reabasto (lead time) del producto; vacío = default del tenant.
    leadTimeDays: item?.leadTimeDays != null ? String(item.leadTimeDays) : '',
    cost: item?.cost != null ? String(item.cost) : '', priceIncludesTax: item?.priceIncludesTax ?? true,
    // feat/public-catalog — categoría (entidad) por id + publicación en tienda pública.
    categoryId: (item?.categoryId ?? null) as string | null,
    isPublic: item?.isPublic ?? false,
    // V130 (§31.4) — GastroGest: producto sugerido (franja de upsell del POS). Solo gastro.
    isSuggested: item?.isSuggested ?? false,
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // V106 — override de precio por sucursal en contexto (solo edición + >1 sucursal).
  const multiBranch = useHasMultipleBranches();
  const [showBranchPricing, setShowBranchPricing] = useState(false);
  // feat/branch-write-require-active — en consolidado (>1 sucursal) capturar stock
  // inicial > 0 no tiene destino → se exige elegir sucursal antes de guardar.
  const blockedByBranch = !isEdit && needsChoice && Number(f.currentStock) > 0;
  const submit = async (e: any) => {
    e.preventDefault(); setErr(null); setBusy(true);
    const body: any = {
      name: f.name, sku: f.sku || undefined, basePrice: Number(f.basePrice), currency: f.currency,
      trackInventory: f.trackInventory,
      lowStockAlertAt: f.lowStockAlertAt ? Number(f.lowStockAlertAt) : undefined,
      // null explícito en edición = volver al default del tenant (V87).
      leadTimeDays: f.leadTimeDays ? Number(f.leadTimeDays) : (isEdit ? null : undefined),
      cost: f.cost ? Number(f.cost) : undefined, priceIncludesTax: f.priceIncludesTax, taxRate,
      // feat/public-catalog — categoría como entidad (null explícito = "Sin categoría",
      // válido para limpiar en edición) + publicación en tienda pública (tenant-wide).
      categoryId: f.categoryId,
      isPublic: f.isPublic,
    };
    // currentStock solo en CREATE (en edit el stock se mueve vía "Ajustar stock").
    // feat/branch-write-require-active — el stock inicial (>0) es una escritura por
    // sucursal: el backend exige branchId SOLO si currentStock > 0. Va la sucursal
    // activa de la sesión. (needsChoice + stock > 0 se bloquea en el botón, abajo.)
    if (!isEdit) {
      body.currentStock = Number(f.currentStock);
      if (Number(f.currentStock) > 0) body.branchId = writeBranchId;
    }
    try {
      const saved: any = isEdit
        ? await api(`/boutique/products/${item.id}`, { method: 'PATCH', body })
        : await api('/boutique/products', { method: 'POST', body });
      // V130 (§31.4) — la marca "sugerido" vive en un endpoint gastro-only
      // (/gastro/suggested/:id, 403 fuera de gastro). Se persiste aparte del catálogo
      // compartido, solo si cambió, y solo en GastroGest.
      if (hasGastro) {
        const pid = isEdit ? item.id : saved?.id;
        const prev = isEdit ? !!item?.isSuggested : false;
        if (pid && f.isSuggested !== prev) {
          await api(`/gastro/suggested/${pid}`, { method: 'PATCH', body: { isSuggested: f.isSuggested } });
        }
      }
      onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); } finally { setBusy(false); }
  };
  return (
    <Modal
      title={isEdit ? 'Editar producto' : 'Nuevo producto'}
      onClose={onClose}
      footer={(
        <div className="flex justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
          <button type="submit" form="product-form" className={btnPrimary} disabled={busy || blockedByBranch}
            title={blockedByBranch ? 'Elige una sucursal concreta en la barra superior para registrar el stock inicial; en vista consolidada no hay destino único.' : undefined}>{busy ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear'}</button>
        </div>
      )}
    >
      <form id="product-form" onSubmit={submit} className="space-y-3">
        <L label="Nombre"><input className="input" required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></L>
        <div className="grid grid-cols-2 gap-3">
          <L label="SKU (opcional)"><input className="input" value={f.sku} onChange={e => setF({ ...f, sku: e.target.value })} /></L>
          <L label="Precio"><input className="input" type="number" min="0" step="0.01" value={f.basePrice} onChange={e => setF({ ...f, basePrice: e.target.value })} /></L>
        </div>
        {isEdit && multiBranch && (
          <button type="button" onClick={() => setShowBranchPricing(true)} className="text-xs font-medium text-slate-600 underline">
            Precio por sucursal →
          </button>
        )}
        <CategorySelectField value={f.categoryId} categories={prodCategories}
          onChange={(id) => setF({ ...f, categoryId: id })} onCategoriesChanged={onCategoriesChanged} />
        {/* feat/public-catalog — publicar el producto en la tienda pública (tenant-wide;
            sin sucursal). Default privado. */}
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" className="mt-0.5" checked={f.isPublic} onChange={e => setF({ ...f, isPublic: e.target.checked })} />
          <span>
            Publicar en la tienda pública
            <span className="block text-[11px] text-slate-400">Visible para tus clientes en el catálogo público (aplica a todo el tenant, no por sucursal).</span>
          </span>
        </label>
        <IvaFields price={f.basePrice} cost={f.cost} includesTax={f.priceIncludesTax} taxRate={taxRate}
          onCost={v => setF({ ...f, cost: v })} onIncludes={v => setF({ ...f, priceIncludesTax: v })} />
        <div className="grid grid-cols-2 gap-3">
          <L label="Moneda"><input className="input" maxLength={3} value={f.currency} onChange={e => setF({ ...f, currency: e.target.value.toUpperCase() })} /></L>
          {!isEdit && <L label="Stock inicial"><input className="input" type="number" min="0" value={f.currentStock} onChange={e => setF({ ...f, currentStock: e.target.value })} /></L>}
        </div>
        {blockedByBranch && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-[11px] text-amber-700 ring-1 ring-amber-200">
            Estás en vista consolidada ("Todas"). Para registrar stock inicial elige una sucursal en la barra superior, o deja el stock inicial en 0.
          </p>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={f.trackInventory} onChange={e => setF({ ...f, trackInventory: e.target.checked })} />
          Controlar inventario
        </label>
        {/* V130 (§31.4) — solo GastroGest: marca el producto para la franja "Sugeridos"
            del POS (upsell). Fuera de gastro no se muestra (el API además valida 403). */}
        {hasGastro && (
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" className="mt-0.5" checked={f.isSuggested} onChange={e => setF({ ...f, isSuggested: e.target.checked })} />
            <span>
              Sugerido en el punto de venta
              <span className="block text-[11px] text-slate-400">Aparece en la franja de sugeridos del POS para upsell (máx 4; se oculta si no hay stock/insumos).</span>
            </span>
          </label>
        )}
        {f.trackInventory && (
          <div className="grid grid-cols-2 gap-3">
            <L label="Alerta de bajo stock (≤)"><input className="input" type="number" min="0" value={f.lowStockAlertAt} onChange={e => setF({ ...f, lowStockAlertAt: e.target.value })} /></L>
            {/* V87 — para la predicción del digest: si el proveedor tarda N días, avisar cuando quede <= N días de venta. */}
            <L label="Días de reabasto (vacío = default)"><input className="input" type="number" min="1" max="60" placeholder="7" value={f.leadTimeDays} onChange={e => setF({ ...f, leadTimeDays: e.target.value })} /></L>
          </div>
        )}
        {/* §21.2 — variantes/tamaños. Requieren el producto creado (necesitan su id). */}
        {isEdit
          ? <VariantsEditor productId={item.id} initial={item.variants ?? []} />
          : <p className="rounded-xl bg-slate-50 px-3 py-2 text-[11px] text-slate-500">¿Tiene tamaños/variantes (Grande/Petiso…)? Crea el producto y edítalo para agregarlas.</p>}
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
      </form>
      {showBranchPricing && isEdit && (
        <ProductBranchPriceModal productId={item.id} productName={item.name} basePrice={Number(f.basePrice)} onClose={() => setShowBranchPricing(false)} />
      )}
    </Modal>
  );
}

// §21.2 — editor de variantes/tamaños de un producto. Persiste por su propio
// endpoint (POST/DELETE /boutique/products/:id/variants). El IVA hereda del producto.
function VariantsEditor({ productId, initial }: { productId: string; initial: any[] }) {
  const [variants, setVariants] = useState<any[]>(initial ?? []);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // T3 — la talla nueva de un producto con existencia se guarda DENTRO del reparto.
  const [pendingReparto, setPendingReparto] = useState<{ name: string; price: number; cost?: number } | null>(null);

  const add = async () => {
    if (!name.trim() || price === '') { setErr('Pon nombre y precio.'); return; }
    setBusy(true); setErr(null);
    const draft = { name: name.trim(), price: Number(price), cost: cost ? Number(cost) : undefined };
    try {
      const v = await api(`/boutique/products/${productId}/variants`, { method: 'POST', body: draft });
      setVariants((vs) => [...vs, v]); setName(''); setPrice(''); setCost('');
    } catch (e: any) {
      if (e?.body?.code === 'requires_reparto') setPendingReparto(draft);
      else setErr(e?.body?.message || e?.message || 'Error');
    } finally { setBusy(false); }
  };
  const afterReparto = async () => {
    setPendingReparto(null); setName(''); setPrice(''); setCost('');
    try { setVariants(await api(`/boutique/products/${productId}/variants`) as any[]); } catch { /* se refresca al reabrir */ }
  };
  const remove = async (id: string) => {
    setBusy(true); setErr(null);
    try { await api(`/boutique/products/variants/${id}`, { method: 'DELETE' }); setVariants((vs) => vs.filter((v) => v.id !== id)); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2 rounded-xl border border-slate-200 p-3">
      <p className="text-xs font-medium text-slate-700">Variantes / tamaños</p>
      <p className="text-[11px] text-slate-400">Si el producto tiene variantes (Grande/Petiso, entera/deslacto…), en el POS se elige una y se cobra su precio. El IVA hereda del producto.</p>
      {variants.length > 0 && (
        <ul className="space-y-1">
          {variants.map((v) => (
            <li key={v.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-1.5 text-sm">
              <span className="text-slate-700">{v.name} <span className="tabular-nums text-slate-400">· ${num2(v.price)}</span></span>
              <button type="button" className="text-[11px] font-medium text-rose-600 hover:underline disabled:opacity-50" disabled={busy} onClick={() => remove(v.id)}>Quitar</button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <input className="input min-w-[8rem] flex-1" placeholder="Nombre (Grande)" value={name} onChange={(e) => setName(e.target.value)} />
        <input className="input w-24" type="number" min="0" step="0.01" placeholder="Precio" value={price} onChange={(e) => setPrice(e.target.value)} />
        <input className="input w-24" type="number" min="0" step="0.01" placeholder="Costo" value={cost} onChange={(e) => setCost(e.target.value)} />
        <button type="button" className={btnGhost} disabled={busy} onClick={add}>Agregar</button>
      </div>
      {err && <p className="text-[11px] text-rose-600">{err}</p>}
      {pendingReparto && (
        <StockRepartoModal productId={productId} newVariants={[pendingReparto]}
          onClose={() => setPendingReparto(null)} onSaved={afterReparto} />
      )}
    </div>
  );
}

const MOVE_TYPE_LABEL: Record<string, string> = {
  entrada: 'Entrada', salida: 'Salida', ajuste: 'Ajuste', venta: 'Venta',
};

function AdjustStockModal({ product, variant = null, writeBranchId, onClose, onSaved, initialDelta = 0 }: { product: any; variant?: { id: string; name: string; onHand?: number } | null; writeBranchId: string | null; onClose: () => void; onSaved: () => void; initialDelta?: number }) {
  // PR-3 / punto 4 — un producto con variantes ABRE con selector de talla (o llega
  // prefijado por el lapiz del renglon). Sin talla elegida, Aplicar deshabilitado.
  // T3 — en POOL (sin repartir) el ajuste es de la existencia compartida: sin selector de talla.
  const poolMode = product.stockMode === 'pool';
  const productVariants: any[] = poolMode ? [] : (product.variants ?? []);
  const [pickedVariantId, setPickedVariantId] = useState<string | null>(variant?.id ?? null);
  const effVariant = variant ?? productVariants.find((v) => v.id === pickedVariantId) ?? null;
  const needsVariant = !effVariant && productVariants.length > 0;
  const baseStock = effVariant ? Number(effVariant.onHand ?? effVariant.available ?? 0) : (product.currentStock || 0);
  const [delta, setDelta] = useState(String(initialDelta || 0));
  const [reason, setReason] = useState('');
  const [type, setType] = useState('ajuste');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [moves, setMoves] = useState<any[]>([]);
  const [loadingMoves, setLoadingMoves] = useState(true);
  // P1 — una escritura por acción: llave estable por apertura del modal (idempotencia).
  const [adjustmentKey] = useState(() => crypto.randomUUID());
  const negative = (Number(delta) || 0) < 0;              // P2 — restar exige motivo
  const reasonMissing = negative && !reason.trim();

  useEffect(() => {
    let cancelled = false;
    setLoadingMoves(true);
    // Punto 6 — historial acotado por SUCURSAL y, si hay talla elegida, por VARIANTE.
    const qs = new URLSearchParams();
    if (writeBranchId) qs.set('branchId', writeBranchId);
    if (effVariant) qs.set('variantId', effVariant.id);
    const q = qs.toString();
    api(`/boutique/products/${product.id}/movements${q ? `?${q}` : ''}`)
      .then((m) => { if (!cancelled) setMoves(m as any[]); })
      .catch(() => { if (!cancelled) setMoves([]); })
      .finally(() => { if (!cancelled) setLoadingMoves(false); });
    return () => { cancelled = true; };
  }, [product.id, writeBranchId, effVariant?.id]);

  const bump = (n: number) => setDelta(String((Number(delta) || 0) + n));
  const preview = baseStock + (Number(delta) || 0);

  const submit = async (e: any) => {
    e.preventDefault(); setErr(null);
    if (reasonMissing) { setErr('Un ajuste que resta stock requiere un motivo.'); return; }
    setBusy(true);
    try {
      await api(`/boutique/products/${product.id}/adjust-stock`, {
        method: 'POST', body: { delta: Number(delta), reason: reason || undefined, type, branchId: writeBranchId, adjustmentKey, ...(effVariant ? { variantId: effVariant.id } : {}) },
      });
      onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Ajustar stock — ${product.name}${effVariant ? ` · ${effVariant.name}` : ''}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        {/* Punto 4 — selector de talla (si el producto tiene variantes y no llego prefijado). */}
        {productVariants.length > 0 && !variant && (
          <L label="Talla / variante">
            <select className="input" value={pickedVariantId ?? ''} onChange={(e) => setPickedVariantId(e.target.value || null)}>
              <option value="">Elige una talla…</option>
              {productVariants.map((v) => (
                <option key={v.id} value={v.id}>{v.name}{v.onHand != null ? ` (${v.onHand})` : ''}</option>
              ))}
            </select>
          </L>
        )}
        {poolMode && (product.variants?.length ?? 0) > 0 && (
          <p className="rounded-lg bg-slate-50 px-3 py-1.5 text-[12px] text-slate-600">Este producto aún no está repartido por talla en esta sucursal: ajustas la existencia compartida. Para llevarla a cada talla usa «Repartir por talla».</p>
        )}
        {needsVariant
          ? <p className="text-sm text-amber-600">Elige una talla para ajustar su existencia.</p>
          : <p className="text-sm text-slate-500">Stock actual{effVariant ? ` (talla ${effVariant.name})` : ''}: <strong className="tabular-nums">{baseStock}</strong></p>}
        <div className="flex flex-wrap gap-1.5">
          {[-10, -5, -1, +1, +5, +10].map((n) => (
            <button type="button" key={n} className={btnGhost} onClick={() => bump(n)}>{n > 0 ? `+${n}` : n}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <L label="Delta (+/-)"><input className="input" type="number" value={delta} onChange={e => setDelta(e.target.value)} /></L>
          <L label="Tipo">
            <select className="input" value={type} onChange={e => setType(e.target.value)}>
              <option value="ajuste">Ajuste</option>
              <option value="entrada">Entrada (compra/recepción)</option>
              <option value="salida">Salida (merma/baja)</option>
            </select>
          </L>
        </div>
        <p className={`text-sm ${preview < 0 ? 'text-rose-600' : 'text-slate-500'}`}>Nuevo stock: <strong className="tabular-nums">{preview}</strong>{preview < 0 && ' — no se permite stock negativo'}</p>
        <L label={negative ? 'Motivo (obligatorio al restar)' : 'Motivo (opcional)'}>
          <input className="input" value={reason} onChange={e => setReason(e.target.value)}
            placeholder={negative ? 'Merma, conteo físico, corrección…' : 'Compra, merma, conteo físico…'} />
        </L>
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex justify-end gap-2"><button type="button" className={btnGhost} onClick={onClose}>Cancelar</button><button className={btnPrimary} disabled={busy || reasonMissing || preview < 0 || needsVariant}>{busy ? 'Guardando…' : 'Aplicar'}</button></div>
      </form>

      {/* Historial de movimientos (trazabilidad por usuario). */}
      <div className="mt-5 border-t border-slate-100 pt-4">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Historial de movimientos</p>
        {loadingMoves ? <p className="text-sm text-slate-400">Cargando…</p> : moves.length === 0 ? (
          <p className="text-sm text-slate-400">Sin movimientos registrados.</p>
        ) : (
          <div className="max-h-48 space-y-1 overflow-y-auto">
            {moves.map((m) => {
              const d = Number(m.delta);
              return (
                <div key={m.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-2">
                    <span className={`tabular-nums font-medium ${d < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{d > 0 ? `+${d}` : d}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{MOVE_TYPE_LABEL[m.type] ?? m.type}</span>
                    <span className="text-slate-500">{m.reason || '—'}</span>
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-400">{m.userName?.trim() || 'Sistema'} · {new Date(m.createdAt).toLocaleString('es-MX')}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

// feat/inventory-management — actualización en LOTE: captura un delta por producto
// (controlado por inventario) + un motivo compartido → /bulk-adjust-stock. Cada
// renglón con delta ≠ 0 registra su propio movimiento (con el usuario).
function BulkAdjustStockModal({ products, writeBranchId, onClose, onSaved }: { products: any[]; writeBranchId: string; onClose: () => void; onSaved: () => void }) {
  const [deltas, setDeltas] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Punto 5 — una fila POR TALLA para productos con variantes (el lote escribe por
  // variante, no product-level). Producto sin variantes = una fila product-level.
  type Row = { key: string; productId: string; productName: string; variantId?: string; variantName?: string; current: number };
  const rows: Row[] = products.flatMap((p) => {
    // T3 — un producto en POOL (sin repartir) se ajusta como producto: una fila sin talla.
    const vs: any[] = p.stockMode === 'pool' ? [] : (p.variants ?? []);
    if (vs.length > 0) return vs.map((v) => ({
      key: `${p.id}:${v.id}`, productId: p.id, productName: p.name, variantId: v.id, variantName: v.name,
      current: Number(v.onHand ?? v.available ?? 0),
    }));
    return [{ key: p.id, productId: p.id, productName: p.name, current: Number(p.currentStock || 0) }];
  });

  const set = (k: string, v: string) => setDeltas((d) => ({ ...d, [k]: v }));
  const pending = rows
    .map((r) => ({ r, delta: Number(deltas[r.key] || 0) }))
    .filter((x) => Number.isFinite(x.delta) && x.delta !== 0);

  const submit = async () => {
    if (pending.length === 0) { setErr('Captura al menos un ajuste.'); return; }
    setBusy(true); setErr(null);
    try {
      await api('/boutique/products/bulk-adjust-stock', {
        method: 'POST',
        body: {
          branchId: writeBranchId,
          items: pending.map((x) => ({ productId: x.r.productId, ...(x.r.variantId ? { variantId: x.r.variantId } : {}), delta: x.delta })),
          reason: reason || undefined,
        },
      });
      onSaved();
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'Error'); } finally { setBusy(false); }
  };

  return (
    <Modal title="Ajuste de stock en lote" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-[12px] text-slate-500">Captura el delta (+/-) de los productos/tallas que quieras ajustar. Los que dejes en 0 se ignoran.</p>
        <L label="Motivo compartido (opcional)"><input className="input" value={reason} onChange={e => setReason(e.target.value)} placeholder="Conteo físico, recepción de compra…" /></L>
        <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-3 py-2">Producto</th><th className="px-3 py-2 text-right">Actual</th><th className="px-3 py-2 text-right">Delta</th><th className="px-3 py-2 text-right">Nuevo</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const d = Number(deltas[r.key] || 0);
                const next = r.current + (Number.isFinite(d) ? d : 0);
                return (
                  <tr key={r.key}>
                    <td className="px-3 py-1.5 text-slate-700">{r.productName}
                      {r.variantName && <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{r.variantName}</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{r.current}</td>
                    <td className="px-3 py-1.5 text-right">
                      <input className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-right text-sm tabular-nums"
                        type="number" value={deltas[r.key] ?? ''} placeholder="0" onChange={(e) => set(r.key, e.target.value)} />
                    </td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${next < 0 ? 'font-semibold text-rose-600' : 'text-slate-700'}`}>{next}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-slate-400">{pending.length} renglón(es) a ajustar</span>
          <div className="flex gap-2">
            <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
            <button type="button" className={btnPrimary} disabled={busy} onClick={submit}>{busy ? 'Aplicando…' : 'Aplicar lote'}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ===========================================================================
// Agenda (appointments) — tabla simple, sin calendario visual (v1.4.1+)
// ===========================================================================
const APPT_STATUS_LABEL: Record<string, string> = {
  SCHEDULED: 'Agendada', CONFIRMED: 'Confirmada', COMPLETED: 'Completada', CANCELLED: 'Cancelada', NO_SHOW: 'No asistió',
};
// Colores por estado para los chips del calendario.
const APPT_STATUS_DOT: Record<string, string> = {
  SCHEDULED: 'bg-sky-500', CONFIRMED: 'bg-emerald-500', COMPLETED: 'bg-slate-400', CANCELLED: 'bg-rose-500', NO_SHOW: 'bg-amber-500',
};
const APPT_STATUS_CHIP: Record<string, string> = {
  SCHEDULED: 'bg-sky-50 text-sky-700 ring-sky-600/15',
  CONFIRMED: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15',
  COMPLETED: 'bg-slate-100 text-slate-500 ring-slate-300',
  CANCELLED: 'bg-rose-50 text-rose-700 ring-rose-600/15',
  NO_SHOW: 'bg-amber-50 text-amber-700 ring-amber-600/15',
};

// Fase 4 (D4) — overlay de reservas en línea (motor de booking, kind=SLOT)
// sobre la agenda de citas. Se pintan en violeta con badge "en línea"; su
// gestión (mover/cancelar/asistencia) vive en Agenda → Reservas.
const ONLINE_CHIP = 'bg-violet-50 text-violet-700 ring-violet-600/20';
const ONLINE_STATUS_LABEL: Record<string, string> = {
  BOOKED: 'Reservada', ATTENDED: 'Asistió', NO_SHOW: 'No asistió', CANCELLED: 'Cancelada',
};
// Mapeo al dominio de estados de cita SOLO para el filtro del dropdown.
const ONLINE_TO_APPT_STATUS: Record<string, string> = {
  BOOKED: 'CONFIRMED', ATTENDED: 'COMPLETED', NO_SHOW: 'NO_SHOW', CANCELLED: 'CANCELLED',
};

// --- helpers de fecha (sin libs; se renderiza en la zona del navegador) ------
const pad2 = (n: number) => String(n).padStart(2, '0');
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const mondayOf = (d: Date) => addDays(startOfDay(d), -((d.getDay() + 6) % 7)); // semana inicia lunes
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const fmtTime = (d: Date) => d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
const fmtMonthYear = (d: Date) => { const s = d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }); return s.charAt(0).toUpperCase() + s.slice(1); };
// datetime-local en hora LOCAL (para prellenar "Nueva cita" al click de un día).
const toDatetimeLocal = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

export function AgendaView() {
  const { user } = useAuth();
  const canWrite = can(user, 'bookings:write');
  const canManage = can(user, 'bookings:manage');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  // #307 — filtro por TIPO de ítem (opcional), en lugar del viejo toggle que
  // partía la vista en dos pestañas. Default = Todos (una sola agenda).
  const [typeFilter, setTypeFilter] = useState<'all' | 'citas' | 'reservas'>('all');
  const [view, setView] = useState<'calendar' | 'table'>('calendar');
  const [calMode, setCalMode] = useState<'month' | 'week'>('month');
  const [cursor, setCursor] = useState<Date>(() => startOfDay(new Date()));
  const [modal, setModal] = useState<{ initialDate?: string } | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  // Reservas del motor de booking: se muestran como ítems (por reserva + sesiones
  // libres) y se GESTIONAN in-situ con SlotDetailModal (misma superficie que la
  // vieja pestaña "Reservas"). Overlay apagado si el rol no tiene bookings:read.
  const [online, setOnline] = useState<any[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [peek, setPeek] = useState<any | null>(null);
  const [slotDetail, setSlotDetail] = useState<Slot | null>(null);
  const [showOfferings, setShowOfferings] = useState(false);
  const [showNewSlot, setShowNewSlot] = useState(false);
  const [showInstructors, setShowInstructors] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows((await api(`/boutique/appointments${status ? `?status=${status}` : ''}`)) as any[]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  // Ventana generosa alrededor del cursor (cubre mes/semana y tabla).
  const winFrom = () => addDays(cursor, -62).toISOString();
  const winTo = () => addDays(cursor, 124).toISOString();
  const loadOnline = async () => {
    try {
      const bks = (await api(`/booking/bookings?from=${winFrom()}&to=${winTo()}&kind=SLOT`)) as any[];
      setOnline(bks.map((b) => ({
        id: `online-${b.id}`, online: true, bookingId: b.id, slotId: b.slotId,
        scheduledAt: b.startsAt, durationMinutes: b.durationMinutes,
        customerName: b.customerName, customerEmail: b.customerEmail, customerPhone: b.customerPhone,
        status: b.status, source: b.source, offeringName: b.offeringName,
      })));
    } catch { setOnline([]); }
  };
  // Sesiones del motor (incluidas las canceladas/vacías) para poder abrir la
  // gestión de CUALQUIER sesión desde la agenda (agregar reserva, cancelar, etc.).
  const loadSlots = () =>
    api<Slot[]>(`/booking/slots?from=${winFrom()}&to=${winTo()}&kind=SLOT&includeCancelled=true`)
      .then(setSlots).catch(() => setSlots([]));
  const loadOfferings = () =>
    api<Offering[]>('/booking/offerings?kind=SLOT').then(setOfferings).catch(() => setOfferings([]));
  const loadInstructors = () =>
    api<Instructor[]>('/booking/instructors').then(setInstructors).catch(() => setInstructors([]));
  useEffect(() => { loadOnline(); loadSlots(); }, [cursor]);
  useEffect(() => { loadOfferings(); loadInstructors(); }, []);

  const setStat = async (id: string, s: string) => {
    await api(`/boutique/appointments/${id}/status`, { method: 'PATCH', body: { status: s } });
    setDetail(null); load();
  };
  const refreshBookingSurfaces = () => { loadOnline(); loadSlots(); };
  // Abre la gestión de una reserva en línea: su SESIÓN (SlotDetailModal). Si la
  // sesión no está en la ventana cargada, cae al peek de solo-lectura.
  const openReserva = (a: any) => {
    const slot: Slot | undefined = a.slot ?? slots.find((s) => s.id === a.slotId);
    if (slot) setSlotDetail(slot); else setPeek(a);
  };
  const pickItem = (a: any) => (a.online ? openReserva(a) : setDetail(a));

  // Citas (manuales) + reservas en línea + SESIONES LIBRES (sin reserva viva),
  // para que nada de la gestión de "Reservas" se pierda al quitar el toggle.
  const merged = useMemo(() => {
    const visibleOnline = status
      ? online.filter((b) => ONLINE_TO_APPT_STATUS[b.status] === status)
      : online;
    // Sesiones OPEN sin ninguna reserva viva → ítem "sesión libre" (gestionable).
    // Se ocultan cuando se filtra por un estado de cita (OPEN no es un estado de cita).
    const bookedSlotIds = new Set(online.map((o) => o.slotId));
    const freeSessions = status ? [] : slots
      .filter((s) => s.status === 'OPEN' && !bookedSlotIds.has(s.id))
      .map((s) => ({
        id: `slot-${s.id}`, online: true, reserva: true, freeSession: true, slot: s, slotId: s.id,
        scheduledAt: s.startsAt, durationMinutes: s.durationMinutes,
        customerName: s.offeringName, offeringName: s.offeringName, status: 'OPEN',
      }));
    let items = [...rows, ...visibleOnline, ...freeSessions];
    if (typeFilter === 'citas') items = items.filter((a) => !a.online);
    else if (typeFilter === 'reservas') items = items.filter((a) => a.online);
    return items.sort((x, y) => +new Date(x.scheduledAt) - +new Date(y.scheduledAt));
  }, [rows, online, slots, status, typeFilter]);

  // Agrupa por día (en hora local del navegador).
  const byDay = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const a of merged) {
      const d = new Date(a.scheduledAt);
      const k = dayKey(d);
      (m.get(k) ?? m.set(k, []).get(k)!).push(a);
    }
    for (const arr of m.values()) arr.sort((x, y) => +new Date(x.scheduledAt) - +new Date(y.scheduledAt));
    return m;
  }, [merged]);

  const moveCursor = (dir: number) => setCursor((c) =>
    calMode === 'month' ? new Date(c.getFullYear(), c.getMonth() + dir, 1) : addDays(c, dir * 7));

  const periodLabel = calMode === 'month'
    ? fmtMonthYear(cursor)
    : (() => { const s = mondayOf(cursor); const e = addDays(s, 6); return `${s.getDate()} – ${e.getDate()} ${fmtMonthYear(e)}`; })();

  const noOfferings = offerings.filter((o) => o.isActive).length === 0;

  return (
    <div>
      <Header title="Agenda" subtitle="Una sola agenda: citas manuales + reservas en línea de la sucursal."
        action={<button className={btnPrimary} onClick={() => setModal({})}>+ Nueva cita</button>} />

      {/* Controles: vista + filtro TIPO + filtro estado + gestión + navegación */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5">
          <button onClick={() => setView('calendar')}
            className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium ${view === 'calendar' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            <CalendarDays className="h-3.5 w-3.5" /> Calendario
          </button>
          <button onClick={() => setView('table')}
            className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium ${view === 'table' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            <List className="h-3.5 w-3.5" /> Tabla
          </button>
        </div>
        {/* #307 — filtro por tipo (opcional), sustituye el toggle de 2 pestañas */}
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5 text-xs">
          {([['all', 'Todos'], ['citas', 'Citas'], ['reservas', 'Reservas']] as const).map(([t, label]) => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`rounded-lg px-2 py-1 font-medium ${typeFilter === t ? 'bg-slate-100 text-slate-900' : 'text-slate-500'}`}>
              {label}
            </button>
          ))}
        </div>
        <select className="input max-w-[10rem]" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Todos los estados</option>
          {Object.keys(APPT_STATUS_LABEL).map(s => <option key={s} value={s}>{APPT_STATUS_LABEL[s]}</option>)}
        </select>

        {/* Gestión del motor de booking (antes en la pestaña "Reservas") */}
        {canManage && (
          <div className="flex items-center gap-2">
            <button className={btnGhost} onClick={() => setShowInstructors(true)}>Personal</button>
            <button className={btnGhost} onClick={() => setShowOfferings(true)}>Tipos de cita</button>
            <button className={btnGhost} onClick={() => setShowNewSlot(true)} disabled={noOfferings}
              title={noOfferings ? 'Primero crea un tipo de cita' : undefined}>+ Nueva sesión</button>
          </div>
        )}

        {view === 'calendar' && (
          <div className="ml-auto flex items-center gap-2">
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5 text-xs">
              <button onClick={() => setCalMode('month')} className={`rounded-lg px-2 py-1 font-medium ${calMode === 'month' ? 'bg-slate-100 text-slate-900' : 'text-slate-500'}`}>Mes</button>
              <button onClick={() => setCalMode('week')} className={`rounded-lg px-2 py-1 font-medium ${calMode === 'week' ? 'bg-slate-100 text-slate-900' : 'text-slate-500'}`}>Semana</button>
            </div>
            <button className={btnGhost} onClick={() => setCursor(startOfDay(new Date()))}>Hoy</button>
            <button className={btnGhost} onClick={() => moveCursor(-1)}><ChevronLeft className="h-4 w-4" /></button>
            <span className="min-w-[8.5rem] text-center text-sm font-medium text-slate-700">{periodLabel}</span>
            <button className={btnGhost} onClick={() => moveCursor(1)}><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      {/* #307 — UNA sola agenda: citas manuales + reservas en línea juntas,
          filtradas por la sucursal activa (top-bar), distinguibles por la
          leyenda. Cada ítem gestiona lo suyo (cita → editar/estado; reserva →
          sesión completa). */}
      <div className="mb-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-300 ring-1 ring-inset ring-slate-300" /> Cita manual
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-violet-300 ring-1 ring-inset ring-violet-300" /> Reserva en línea ⚡
        </span>
      </div>

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : view === 'calendar' ? (
        calMode === 'month'
          ? <MonthGrid cursor={cursor} byDay={byDay} onPickDay={(d) => setModal({ initialDate: toDatetimeLocal((() => { const x = new Date(d); x.setHours(12, 0, 0, 0); return x; })()) })} onPickAppt={pickItem} />
          : <WeekGrid cursor={cursor} byDay={byDay} onPickDay={(d) => setModal({ initialDate: toDatetimeLocal((() => { const x = new Date(d); x.setHours(12, 0, 0, 0); return x; })()) })} onPickAppt={pickItem} />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-2">Fecha</th><th className="px-4 py-2">Cliente / sesión</th><th className="px-4 py-2">Estado</th><th className="px-4 py-2">Contacto</th><th className="px-4 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {merged.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Sin citas ni reservas.</td></tr>}
              {merged.map((a) => a.online ? (
                <tr key={a.id} className="bg-violet-50/30">
                  <td className="px-4 py-2">{new Date(a.scheduledAt).toLocaleString('es-MX')}</td>
                  <td className="px-4 py-2 text-slate-700">
                    {a.freeSession ? a.offeringName : a.customerName}
                    <span className="ml-2 rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                      {a.freeSession ? 'sesión libre' : 'en línea'}
                    </span>
                  </td>
                  <td className="px-4 py-2">{a.freeSession ? a.offeringName : `${ONLINE_STATUS_LABEL[a.status] ?? a.status} · ${a.offeringName}`}</td>
                  <td className="px-4 py-2">{a.freeSession ? '' : <ContactActions name={a.customerName} phone={a.customerPhone} email={a.customerEmail} />}</td>
                  <td className="px-4 py-2 text-right">
                    <button className={btnGhost} onClick={() => openReserva(a)}>Gestionar</button>
                  </td>
                </tr>
              ) : (
                <tr key={a.id}>
                  <td className="px-4 py-2">{new Date(a.scheduledAt).toLocaleString('es-MX')}</td>
                  <td className="px-4 py-2 text-slate-700">{a.customerName ?? <span className="font-mono text-[11px] text-slate-500">{a.customerContactId.slice(0, 8)}…</span>}</td>
                  <td className="px-4 py-2">{APPT_STATUS_LABEL[a.status] ?? a.status}</td>
                  <td className="px-4 py-2"><ContactActions name={a.customerName} phone={a.customerPhone} email={a.customerEmail} /></td>
                  <td className="px-4 py-2 text-right space-x-1">
                    {a.status === 'SCHEDULED' && <button className={btnGhost} onClick={() => setStat(a.id, 'CONFIRMED')}>Confirmar</button>}
                    {(a.status === 'SCHEDULED' || a.status === 'CONFIRMED') && <button className={btnGhost} onClick={() => setStat(a.id, 'COMPLETED')}>Completar</button>}
                    {a.status !== 'CANCELLED' && a.status !== 'COMPLETED' && <button className={btnGhost} onClick={() => setStat(a.id, 'CANCELLED')}>Cancelar</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && <AppointmentModal initialDate={modal.initialDate} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {detail && <AppointmentDetail appt={detail} onClose={() => setDetail(null)} onStatus={setStat} />}
      {peek && <OnlineBookingPeek booking={peek} onClose={() => setPeek(null)}
                 onGoManage={() => { const s = slots.find((x) => x.id === peek.slotId); setPeek(null); if (s) setSlotDetail(s); }} />}
      {slotDetail && (
        <SlotDetailModal slot={slotDetail} kind="SLOT" canWrite={canWrite} canManage={canManage}
          offerings={offerings.filter((o) => o.isActive)} instructors={instructors.filter((i) => i.isActive)}
          onClose={() => setSlotDetail(null)} onChanged={refreshBookingSurfaces} />
      )}
      {showOfferings && (
        <OfferingsModal kind="SLOT" offerings={offerings} instructors={instructors.filter((i) => i.isActive)}
          resourceTypes={[]} onClose={() => setShowOfferings(false)}
          onChanged={() => { loadOfferings(); loadSlots(); }} />
      )}
      {showNewSlot && (
        <SlotModal kind="SLOT" offerings={offerings.filter((o) => o.isActive)} instructors={instructors.filter((i) => i.isActive)}
          onClose={() => setShowNewSlot(false)} onSaved={() => { setShowNewSlot(false); refreshBookingSurfaces(); }} />
      )}
      {showInstructors && (
        <InstructorsModal kind="SLOT" instructors={instructors} onClose={() => setShowInstructors(false)}
          onChanged={() => { loadInstructors(); loadOfferings(); }} />
      )}
    </div>
  );
}

// --- Calendario: cuadrícula de mes -----------------------------------------
function MonthGrid({ cursor, byDay, onPickDay, onPickAppt }: {
  cursor: Date; byDay: Map<string, any[]>; onPickDay: (d: Date) => void; onPickAppt: (a: any) => void;
}) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = mondayOf(first);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = startOfDay(new Date());

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {WEEKDAYS.map((w) => <div key={w} className="py-2">{w}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const appts = byDay.get(dayKey(d)) ?? [];
          return (
            <div key={i}
              className={`min-h-[92px] cursor-pointer border-b border-r border-slate-100 p-1.5 transition hover:bg-slate-50/70 ${inMonth ? '' : 'bg-slate-50/40 text-slate-300'}`}
              onClick={() => onPickDay(d)}>
              <div className={`mb-1 flex items-center justify-between text-[11px] ${sameDay(d, today) ? 'font-bold text-slate-900' : inMonth ? 'text-slate-500' : 'text-slate-300'}`}>
                <span className={sameDay(d, today) ? 'grid h-5 w-5 place-items-center rounded-full bg-slate-900 text-white' : ''}>{d.getDate()}</span>
              </div>
              <div className="space-y-0.5">
                {appts.slice(0, 3).map((a) => (
                  <button key={a.id} onClick={(e) => { e.stopPropagation(); onPickAppt(a); }}
                    title={a.online ? `Reserva en línea · ${a.offeringName}` : undefined}
                    className={`flex w-full items-center gap-1 truncate rounded px-1 py-0.5 text-left text-[10px] ring-1 ring-inset ${a.online ? ONLINE_CHIP : (APPT_STATUS_CHIP[a.status] ?? 'bg-slate-50 text-slate-600 ring-slate-200')}`}>
                    <span className="tabular-nums opacity-70">{fmtTime(new Date(a.scheduledAt))}</span>
                    <span className="truncate">{a.customerName ?? 'Cliente'}</span>
                    {a.online && <span className="ml-auto shrink-0 opacity-70">⚡</span>}
                  </button>
                ))}
                {appts.length > 3 && <p className="px-1 text-[10px] text-slate-400">+{appts.length - 3} más</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Calendario: vista semana (7 columnas) ----------------------------------
function WeekGrid({ cursor, byDay, onPickDay, onPickAppt }: {
  cursor: Date; byDay: Map<string, any[]>; onPickDay: (d: Date) => void; onPickAppt: (a: any) => void;
}) {
  const start = mondayOf(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const today = startOfDay(new Date());
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
      {days.map((d, i) => {
        const appts = byDay.get(dayKey(d)) ?? [];
        return (
          <div key={i} className="min-h-[180px] rounded-xl border border-slate-200 bg-white p-2">
            <button onClick={() => onPickDay(d)} className="mb-2 w-full text-left">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{WEEKDAYS[i]}</p>
              <p className={`text-sm ${sameDay(d, today) ? 'font-bold text-slate-900' : 'text-slate-600'}`}>{d.getDate()}</p>
            </button>
            <div className="space-y-1">
              {appts.length === 0 && <p className="text-[10px] text-slate-300">—</p>}
              {appts.map((a) => (
                <button key={a.id} onClick={() => onPickAppt(a)}
                  title={a.online ? `Reserva en línea · ${a.offeringName}` : undefined}
                  className={`block w-full truncate rounded px-1.5 py-1 text-left text-[10px] ring-1 ring-inset ${a.online ? ONLINE_CHIP : (APPT_STATUS_CHIP[a.status] ?? 'bg-slate-50 text-slate-600 ring-slate-200')}`}>
                  <span className="tabular-nums opacity-70">{fmtTime(new Date(a.scheduledAt))}</span> {a.customerName ?? 'Cliente'}{a.online ? ' ⚡' : ''}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- Detalle de cita (modal) -----------------------------------------------
function AppointmentDetail({ appt, onClose, onStatus }: { appt: any; onClose: () => void; onStatus: (id: string, s: string) => void }) {
  const d = new Date(appt.scheduledAt);
  return (
    <Modal title="Detalle de cita" onClose={onClose}>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${APPT_STATUS_DOT[appt.status] ?? 'bg-slate-400'}`} />
          <span className="font-medium text-slate-800">{APPT_STATUS_LABEL[appt.status] ?? appt.status}</span>
        </div>
        <p className="text-slate-600"><span className="text-slate-400">Fecha:</span> {d.toLocaleString('es-MX')}</p>
        <p className="text-slate-600"><span className="text-slate-400">Cliente:</span> {appt.customerName ?? appt.customerContactId?.slice(0, 8) + '…'}</p>
        {appt.notes && <p className="text-slate-600"><span className="text-slate-400">Notas:</span> {appt.notes}</p>}
        <div className="pt-1"><ContactActions name={appt.customerName} phone={appt.customerPhone} email={appt.customerEmail} /></div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
          {appt.status === 'SCHEDULED' && <button className={btnGhost} onClick={() => onStatus(appt.id, 'CONFIRMED')}>Confirmar</button>}
          {(appt.status === 'SCHEDULED' || appt.status === 'CONFIRMED') && <button className={btnGhost} onClick={() => onStatus(appt.id, 'COMPLETED')}>Completar</button>}
          {appt.status !== 'CANCELLED' && appt.status !== 'COMPLETED' && <button className={btnGhost} onClick={() => onStatus(appt.id, 'CANCELLED')}>Cancelar</button>}
        </div>
      </div>
    </Modal>
  );
}

// --- Detalle de reserva en línea (overlay D4 — solo lectura) -----------------
// La gestión (mover/cancelar/asistencia) vive en Agenda → Reservas; aquí solo
// se consulta para que el staff vea ambas fuentes sin cambiar de vista.
function OnlineBookingPeek({ booking, onClose, onGoManage }: { booking: any; onClose: () => void; onGoManage: () => void }) {
  const d = new Date(booking.scheduledAt);
  return (
    <Modal title="Reserva en línea" onClose={onClose}>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">en línea</span>
          <span className="font-medium text-slate-800">{ONLINE_STATUS_LABEL[booking.status] ?? booking.status}</span>
          <span className="text-[11px] text-slate-400">({booking.source === 'PUBLIC' ? 'reservó el cliente' : 'registrada en mostrador'})</span>
        </div>
        <p className="text-slate-600"><span className="text-slate-400">Servicio:</span> {booking.offeringName}</p>
        <p className="text-slate-600"><span className="text-slate-400">Fecha:</span> {d.toLocaleString('es-MX')} · {booking.durationMinutes} min</p>
        <p className="text-slate-600"><span className="text-slate-400">Cliente:</span> {booking.customerName}</p>
        <div className="pt-1"><ContactActions name={booking.customerName} phone={booking.customerPhone} email={booking.customerEmail} /></div>
        <div className="flex justify-end border-t border-slate-100 pt-3">
          <button className={btnGhost} onClick={onGoManage}>Gestionar en Reservas</button>
        </div>
      </div>
    </Modal>
  );
}

function useServices() {
  const [services, setServices] = useState<any[]>([]);
  useEffect(() => { api('/boutique/services').then(r => setServices(r as any[])).catch(() => setServices([])); }, []);
  return services;
}

function AppointmentModal({ onClose, onSaved, initialDate }: { onClose: () => void; onSaved: () => void; initialDate?: string }) {
  const services = useServices();
  const { createContact } = useContacts();
  const [f, setF] = useState({ customerContactId: '', serviceId: '', scheduledAt: initialDate ?? '', notes: '' });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // P3 — alta rápida de cliente sin salir del modal. Si el tenant no tiene
  // clientes, esto desbloquea el flujo (antes "Sin resultados" era callejón sin
  // salida). Tras crear, queda seleccionado y se puede agendar de una.
  const [creating, setCreating] = useState(false);
  const [nc, setNc] = useState({ firstName: '', phone: '', email: '' });
  const [ncBusy, setNcBusy] = useState(false);
  const [ncErr, setNcErr] = useState<string | null>(null);

  const submitNewClient = async () => {
    if (!nc.firstName.trim()) { setNcErr('El nombre es obligatorio.'); return; }
    if (!nc.phone.trim() && !nc.email.trim()) { setNcErr('Agrega teléfono o correo.'); return; }
    setNcBusy(true); setNcErr(null);
    try {
      // email vacío rompe @IsEmail del backend → mándalo solo si viene.
      const created = await createContact({
        firstName: nc.firstName.trim(),
        phone: nc.phone.trim() || undefined,
        email: nc.email.trim() || undefined,
      });
      setF((prev) => ({ ...prev, customerContactId: created.id }));
      setCreating(false); setNc({ firstName: '', phone: '', email: '' });
    } catch (e2: any) { setNcErr(e2?.body?.message || e2?.message || 'No se pudo crear el cliente'); }
    finally { setNcBusy(false); }
  };

  const submit = async (e: any) => {
    e.preventDefault(); setErr(null);
    if (!f.customerContactId) { setErr('Selecciona un cliente.'); return; }
    setBusy(true);
    try {
      await api('/boutique/appointments', { method: 'POST', body: {
        customerContactId: f.customerContactId.trim(), serviceId: f.serviceId,
        scheduledAt: new Date(f.scheduledAt).toISOString(), notes: f.notes || undefined,
      } });
      onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); } finally { setBusy(false); }
  };
  return (
    <Modal title="Nueva cita" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <L label="Cliente">
          {creating ? (
            <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
              <p className="text-[12px] font-medium text-slate-600">Nuevo cliente</p>
              <input className="input" placeholder="Nombre *" value={nc.firstName}
                     onChange={e => setNc({ ...nc, firstName: e.target.value })} maxLength={100} autoFocus />
              <div className="grid grid-cols-2 gap-2">
                <input className="input" placeholder="Teléfono" value={nc.phone}
                       onChange={e => setNc({ ...nc, phone: e.target.value })} maxLength={50} />
                <input className="input" placeholder="Correo" type="email" value={nc.email}
                       onChange={e => setNc({ ...nc, email: e.target.value })} maxLength={255} />
              </div>
              {ncErr && <p className="alert-over rounded-xl px-3 py-2 text-xs">{ncErr}</p>}
              <div className="flex justify-end gap-2">
                <button type="button" className={btnGhost} onClick={() => { setCreating(false); setNcErr(null); }}>Cancelar</button>
                <button type="button" className={btnPrimary} disabled={ncBusy} onClick={submitNewClient}>
                  {ncBusy ? 'Creando…' : 'Crear y seleccionar'}
                </button>
              </div>
            </div>
          ) : (
            <ContactPicker value={f.customerContactId || null}
              onChange={id => setF({ ...f, customerContactId: id ?? '' })}
              onRequestCreate={() => { setCreating(true); setNcErr(null); }} />
          )}
        </L>
        <L label="Servicio"><select className="input" required value={f.serviceId} onChange={e => setF({ ...f, serviceId: e.target.value })}><option value="">Selecciona…</option>{services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></L>
        <L label="Fecha y hora"><input className="input" type="datetime-local" required value={f.scheduledAt} onChange={e => setF({ ...f, scheduledAt: e.target.value })} /></L>
        <L label="Notas"><textarea className="input" rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></L>
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex justify-end gap-2"><button type="button" className={btnGhost} onClick={onClose}>Cancelar</button><button className={btnPrimary} disabled={busy || creating}>{busy ? 'Guardando…' : 'Crear'}</button></div>
      </form>
    </Modal>
  );
}

// ===========================================================================
// Historial (service_records)
// ===========================================================================
export function HistoryView() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [followUp, setFollowUp] = useState<any | null>(null);
  const [filterContact, setFilterContact] = useState('');
  // §17.2.b — soft-delete: toggle "mostrar archivadas" + selección múltiple.
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const load = async (contactOverride?: string, archivedOverride?: boolean) => {
    const fc = contactOverride !== undefined ? contactOverride : filterContact;
    const showArch = archivedOverride !== undefined ? archivedOverride : showArchived;
    setLoading(true);
    setSelected(new Set());
    const qs = new URLSearchParams();
    if (fc) qs.set('customerContactId', fc);
    // Default oculta archivadas; el toggle muestra TODAS (activas + archivadas).
    if (showArch) qs.set('archived', 'all');
    const q = qs.toString();
    try { setRows((await api(`/boutique/service-records${q ? `?${q}` : ''}`)) as any[]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleSel = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const setArchived = async (id: string, archived: boolean) => {
    setBusy(true);
    try { await api(`/boutique/service-records/${id}/${archived ? 'archive' : 'unarchive'}`, { method: 'PATCH' }); await load(); }
    finally { setBusy(false); }
  };
  const archiveSelected = async () => {
    if (!selected.size) return;
    setBusy(true);
    try { await api('/boutique/service-records/archive', { method: 'POST', body: { ids: [...selected] } }); await load(); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <Header title="Historial" subtitle="Servicios prestados a clientes."
        action={<button className={btnPrimary} onClick={() => setOpen(true)}>+ Registrar servicio</button>} />
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="max-w-xs flex-1">
          <ContactPicker value={filterContact || null} placeholder="Filtrar por cliente…"
            onChange={id => { const v = id ?? ''; setFilterContact(v); load(v); }} />
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600">
          <input type="checkbox" checked={showArchived}
            onChange={e => { setShowArchived(e.target.checked); load(undefined, e.target.checked); }} />
          Mostrar archivadas
        </label>
      </div>
      {/* §17.2.b — barra de acción en lote cuando hay selección. */}
      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm">
          <span className="text-slate-600">{selected.size} seleccionada{selected.size === 1 ? '' : 's'}</span>
          <div className="flex gap-2">
            <button className={btnGhost} onClick={() => setSelected(new Set())}>Limpiar</button>
            <button className={btnPrimary} disabled={busy} onClick={archiveSelected}>
              <Archive className="mr-1 inline h-3.5 w-3.5" /> Archivar seleccionadas
            </button>
          </div>
        </div>
      )}
      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="w-8 px-4 py-2"></th><th className="px-4 py-2">Fecha</th><th className="px-4 py-2">Cliente</th><th className="px-4 py-2">Observaciones</th><th className="px-4 py-2">Seguimiento</th><th className="px-4 py-2">Contacto</th><th className="px-4 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Sin registros.</td></tr>}
              {rows.map((r) => {
                const archived = !!r.archivedAt;
                return (
                <tr key={r.id} className={archived ? 'bg-slate-50/60 text-slate-400' : ''}>
                  <td className="px-4 py-2">
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} aria-label="Seleccionar" />
                  </td>
                  <td className="px-4 py-2">
                    {new Date(r.performedAt).toLocaleDateString('es-MX')}
                    {archived && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">Archivado</span>}
                  </td>
                  <td className="px-4 py-2 text-slate-700">{r.customerName ?? <span className="font-mono text-[11px] text-slate-500">{r.customerContactId.slice(0, 8)}…</span>}</td>
                  <td className="px-4 py-2 text-slate-600">{r.observations ?? '—'}</td>
                  <td className="px-4 py-2">
                    {r.followUpAt ? (
                      <span className="inline-flex items-center gap-2">
                        <button
                          className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/15 hover:bg-amber-100"
                          title="Editar seguimiento" onClick={() => setFollowUp(r)}>
                          <Bell className="h-3.5 w-3.5" /> {new Date(r.followUpAt).toLocaleDateString('es-MX')}
                          {r.followUpRecurDays ? ` · cada ${r.followUpRecurDays}d` : ''}
                        </button>
                        <a href="/recordatorios" className="text-[11px] text-slate-400 underline hover:text-slate-700">ver</a>
                      </span>
                    ) : (
                      <button className={btnGhost} onClick={() => setFollowUp(r)}>+ Fijar</button>
                    )}
                  </td>
                  <td className="px-4 py-2"><ContactActions name={r.customerName} phone={r.customerPhone} email={r.customerEmail} /></td>
                  <td className="px-4 py-2 text-right">
                    {archived ? (
                      <button className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100" disabled={busy} onClick={() => setArchived(r.id, false)}>
                        <ArchiveRestore className="h-3.5 w-3.5" /> Desarchivar
                      </button>
                    ) : (
                      <button className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100" disabled={busy} onClick={() => setArchived(r.id, true)} title="Archivar (no se borra)">
                        <Archive className="h-3.5 w-3.5" /> Archivar
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {open && <RecordModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {followUp && <FollowUpModal record={followUp} onClose={() => setFollowUp(null)} onSaved={() => { setFollowUp(null); load(); }} />}
    </div>
  );
}

// v1.4.6 — Historial es el ORIGEN del recordatorio. Al fijar un "próximo
// seguimiento" (fecha) se crea/edita un customer_reminders PENDING ligado al
// registro. Canal + nota + "repetir cada N días" configuran ese recordatorio.
interface FollowUp { followUpAt: string; followUpChannel: string; followUpNote: string; followUpRecurDays: string; }
const emptyFollowUp = (): FollowUp => ({ followUpAt: '', followUpChannel: 'WHATSAPP', followUpNote: '', followUpRecurDays: '' });

/** Cuerpo del seguimiento para el POST/PATCH de service-records. */
function followUpPayload(v: FollowUp): Record<string, unknown> {
  if (!v.followUpAt) return {};
  return {
    followUpAt: new Date(v.followUpAt).toISOString(),
    followUpChannel: v.followUpChannel,
    followUpNote: v.followUpNote.trim() || undefined,
    followUpRecurDays: v.followUpRecurDays ? Number(v.followUpRecurDays) : undefined,
  };
}

function FollowUpFields({ v, onChange }: { v: FollowUp; onChange: (v: FollowUp) => void }) {
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
      <L label="Próximo seguimiento (opcional)">
        <input className="input" type="date" value={v.followUpAt} onChange={e => onChange({ ...v, followUpAt: e.target.value })} />
      </L>
      {v.followUpAt && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <L label="Canal">
              <select className="input" value={v.followUpChannel} onChange={e => onChange({ ...v, followUpChannel: e.target.value })}>
                <option value="WHATSAPP">WhatsApp</option><option value="EMAIL">Email</option><option value="SMS">SMS</option>
              </select>
            </L>
            <L label="Repetir cada (días)">
              <input className="input" type="number" min={1} placeholder="no repetir" value={v.followUpRecurDays} onChange={e => onChange({ ...v, followUpRecurDays: e.target.value })} />
            </L>
          </div>
          <L label="Nota (mensaje sugerido)">
            <input className="input" value={v.followUpNote} placeholder="Es hora de tu próxima visita…" onChange={e => onChange({ ...v, followUpNote: e.target.value })} />
          </L>
          <p className="text-[10px] text-slate-400">Crea un recordatorio en <strong>Recordatorios</strong> para esa fecha. Si es recurrente, al marcarlo enviado se genera el siguiente.</p>
        </>
      )}
    </div>
  );
}

function RecordModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const services = useServices();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [f, setF] = useState({ customerContactId: '', serviceId: '', performedAt: today, observations: '' });
  const [fu, setFu] = useState<FollowUp>(emptyFollowUp());
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: any) => {
    e.preventDefault(); setErr(null);
    if (!f.customerContactId) { setErr('Selecciona un cliente.'); return; }
    setBusy(true);
    try {
      await api('/boutique/service-records', { method: 'POST', body: {
        customerContactId: f.customerContactId.trim(), serviceId: f.serviceId,
        performedAt: f.performedAt ? new Date(f.performedAt).toISOString() : undefined,
        observations: f.observations || undefined,
        ...followUpPayload(fu),
      } });
      onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); } finally { setBusy(false); }
  };
  return (
    <Modal title="Registrar servicio" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <L label="Cliente"><ContactPicker value={f.customerContactId || null} onChange={id => setF({ ...f, customerContactId: id ?? '' })} /></L>
        <L label="Servicio"><select className="input" required value={f.serviceId} onChange={e => setF({ ...f, serviceId: e.target.value })}><option value="">Selecciona…</option>{services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></L>
        <L label="Fecha"><input className="input" type="date" value={f.performedAt} onChange={e => setF({ ...f, performedAt: e.target.value })} /></L>
        <L label="Observaciones"><textarea className="input" rows={2} value={f.observations} onChange={e => setF({ ...f, observations: e.target.value })} /></L>
        <FollowUpFields v={fu} onChange={setFu} />
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex justify-end gap-2"><button type="button" className={btnGhost} onClick={onClose}>Cancelar</button><button className={btnPrimary} disabled={busy}>{busy ? 'Guardando…' : 'Registrar'}</button></div>
      </form>
    </Modal>
  );
}

/** v1.4.6 — fijar/editar/limpiar el seguimiento de un registro de Historial ya
 *  creado. Guardar → upsert del recordatorio; Limpiar → borra el PENDING. */
function FollowUpModal({ record, onClose, onSaved }: { record: any; onClose: () => void; onSaved: () => void }) {
  const [fu, setFu] = useState<FollowUp>(() => ({
    followUpAt: record.followUpAt ? new Date(record.followUpAt).toISOString().slice(0, 10) : '',
    followUpChannel: record.followUpChannel ?? 'WHATSAPP',
    followUpNote: '',
    followUpRecurDays: record.followUpRecurDays != null ? String(record.followUpRecurDays) : '',
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/boutique/service-records/${record.id}`, { method: 'PATCH', body: followUpPayload(fu) });
      onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); } finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/boutique/service-records/${record.id}`, { method: 'PATCH', body: { followUpAt: null } });
      onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); } finally { setBusy(false); }
  };
  return (
    <Modal title="Seguimiento del cliente" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">{record.customerName ?? 'Cliente'} — registro del {new Date(record.performedAt).toLocaleDateString('es-MX')}.</p>
        <FollowUpFields v={fu} onChange={setFu} />
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex items-center justify-between gap-2">
          {record.followUpAt
            ? <button type="button" className="text-xs font-medium text-rose-600 hover:text-rose-700 disabled:opacity-50" disabled={busy} onClick={clear}>Limpiar seguimiento</button>
            : <span />}
          <div className="flex gap-2">
            <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
            <button type="button" className={btnPrimary} disabled={busy || !fu.followUpAt} onClick={save}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ===========================================================================
// Recordatorios (customer_reminders) — v1.4.6 reminders engine
// ===========================================================================
const REMINDER_TYPE_LABEL: Record<string, string> = {
  FOLLOW_UP: 'Seguimiento', APPOINTMENT_REMINDER: 'Recordatorio de cita',
  BIRTHDAY: 'Cumpleaños', CUSTOM: 'Personalizado',
};
const CHANNEL_LABEL: Record<string, string> = { EMAIL: 'Email', WHATSAPP: 'WhatsApp', SMS: 'SMS' };
const REMINDER_STATUS_CHIP: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700 ring-amber-600/15',
  SENT: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15',
  CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-300',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-600/15',
};
const REMINDER_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pendiente', SENT: 'Enviado', CANCELLED: 'Cancelado', FAILED: 'Falló',
};
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

const REMINDER_FILTERS: Array<{ v: string; l: string }> = [
  { v: 'PENDING', l: 'Pendientes' }, { v: 'SENT', l: 'Enviados' }, { v: 'ALL', l: 'Todos' },
];

export function RemindersView() {
  const [cursor, setCursor] = useState<Date>(() => startOfDay(new Date()));
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING'); // default: pendientes
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggest, setShowSuggest] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const weekStart = useMemo(() => mondayOf(cursor), [cursor]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const loadWeek = async () => {
    setLoading(true);
    const from = weekStart.toISOString();
    const to = endOfDay(addDays(weekStart, 6)).toISOString();
    const qs = new URLSearchParams({ from, to });
    if (statusFilter !== 'ALL') qs.set('status', statusFilter);
    try {
      setRows((await api(`/boutique/reminders?${qs.toString()}`)) as any[]);
    } finally { setLoading(false); }
  };
  const loadSuggestions = async () => {
    try { setSuggestions(((await api('/boutique/reminders/suggestions?days=30')) as any).rows ?? []); }
    catch { setSuggestions([]); }
  };
  useEffect(() => { loadWeek(); }, [weekStart, statusFilter]);
  useEffect(() => { loadSuggestions(); }, []);

  const markSent = async (id: string) => {
    setBusyId(id);
    try { await api(`/boutique/reminders/${id}/sent`, { method: 'PATCH' }); await loadWeek(); }
    finally { setBusyId(null); }
  };

  // Crea un recordatorio de seguimiento PENDING para un cliente sugerido por cadencia.
  const scheduleFollowUp = async (s: any) => {
    setBusyId(s.customerContactId);
    try {
      await api('/boutique/reminders', { method: 'POST', body: {
        customerContactId: s.customerContactId,
        reminderType: 'FOLLOW_UP',
        channel: s.customerPhone ? 'WHATSAPP' : 'EMAIL',
        scheduledFor: new Date().toISOString(),
        subject: 'Seguimiento',
        message: `Hace ${s.daysSince} días de tu última visita. ¡Nos encantaría verte de nuevo!`,
      } });
      await Promise.all([loadWeek(), loadSuggestions()]);
    } finally { setBusyId(null); }
  };

  // §17.2.b — descartar (snooze) un cliente de las sugerencias. Persistente:
  // no reaparece hasta vencer la vigencia (default 30d server-side).
  const dismissSuggestion = async (s: any) => {
    setBusyId(s.customerContactId);
    try {
      await api('/boutique/reminders/suggestions/dismiss', { method: 'POST', body: {
        customerContactId: s.customerContactId,
      } });
      await loadSuggestions();
    } finally { setBusyId(null); }
  };

  const byDay = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const r of rows) {
      const k = dayKey(new Date(r.scheduledFor));
      (m.get(k) ?? m.set(k, []).get(k)!).push(r);
    }
    for (const arr of m.values()) arr.sort((a, b) => +new Date(a.scheduledFor) - +new Date(b.scheduledFor));
    return m;
  }, [rows]);

  const weekLabel = `${weekStart.getDate()} – ${addDays(weekStart, 6).getDate()} ${fmtMonthYear(addDays(weekStart, 6))}`;

  return (
    <div>
      <Header title="Recordatorios" subtitle="Seguimientos de la semana por cliente — contacta y marca enviado." />

      {/* Sugeridos por cadencia (clientes sin actividad reciente) */}
      {suggestions.length > 0 && (
        <section className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
          <button className="flex w-full items-center justify-between" onClick={() => setShowSuggest((v) => !v)}>
            <span className="flex items-center gap-2 font-medium text-amber-900">
              <Bell className="h-4 w-4" /> {suggestions.length} cliente{suggestions.length === 1 ? '' : 's'} sin actividad reciente
            </span>
            <span className="text-xs text-amber-700">{showSuggest ? 'Ocultar' : 'Ver'}</span>
          </button>
          {showSuggest && (
            <ul className="mt-3 space-y-2">
              {suggestions.map((s) => (
                <li key={s.customerContactId} className="flex flex-wrap items-center gap-2 rounded-xl bg-white px-3 py-2 ring-1 ring-amber-100">
                  <span className="font-medium text-slate-800">{s.customerName ?? <span className="font-mono text-[11px] text-slate-500">{s.customerContactId.slice(0, 8)}…</span>}</span>
                  <span className="text-[11px] text-slate-500">última actividad hace {s.daysSince} días</span>
                  <div className="ml-auto flex items-center gap-1.5">
                    <ContactActions name={s.customerName} phone={s.customerPhone} email={s.customerEmail}
                      message="hace tiempo que no te vemos, ¿agendamos tu próxima visita?" subject="Te extrañamos" />
                    <button className={btnGhost} disabled={busyId === s.customerContactId} onClick={() => scheduleFollowUp(s)}>
                      + Recordatorio
                    </button>
                    <button className="rounded-xl px-2 py-1.5 text-xs font-medium text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      title="Descartar por un tiempo (no reaparece hasta vencer la vigencia)"
                      disabled={busyId === s.customerContactId} onClick={() => dismissSuggestion(s)}>
                      Descartar
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Filtro de estado + navegación de semana */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700">
          <CalendarDays className="h-4 w-4 text-slate-400" /> Semana del {weekLabel}
        </span>
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5 text-xs">
          {REMINDER_FILTERS.map((f) => (
            <button key={f.v} onClick={() => setStatusFilter(f.v)}
              className={`rounded-lg px-2.5 py-1 font-medium ${statusFilter === f.v ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
              {f.l}
            </button>
          ))}
        </div>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{rows.length} esta semana</span>
        <div className="ml-auto flex items-center gap-2">
          <button className={btnGhost} onClick={() => setCursor(startOfDay(new Date()))}>Hoy</button>
          <button className={btnGhost} onClick={() => setCursor(addDays(weekStart, -7))}><ChevronLeft className="h-4 w-4" /></button>
          <button className={btnGhost} onClick={() => setCursor(addDays(weekStart, 7))}><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {days.map((d, i) => {
            const list = byDay.get(dayKey(d)) ?? [];
            const isToday = sameDay(d, new Date());
            return (
              <div key={i} className={`rounded-2xl border bg-white p-3 ${isToday ? 'border-slate-900' : 'border-slate-200'}`}>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-800">{WEEKDAYS[i]} {d.getDate()}</p>
                  {isToday && <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-medium text-white">Hoy</span>}
                </div>
                {list.length === 0 ? (
                  <p className="py-3 text-center text-[11px] text-slate-300">Sin recordatorios</p>
                ) : (
                  <ul className="space-y-2">
                    {list.map((r) => (
                      <li key={r.id} className="rounded-xl border border-slate-100 bg-slate-50/60 p-2.5">
                        <div className="mb-1 flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-xs tabular-nums text-slate-500">{fmtTime(new Date(r.scheduledFor))}</span>
                          <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${REMINDER_STATUS_CHIP[r.status] ?? ''}`}>
                            {REMINDER_STATUS_LABEL[r.status] ?? r.status}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-800">
                          {r.customerName ?? <span className="font-mono text-[11px] text-slate-500">{r.customerContactId.slice(0, 8)}…</span>}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {REMINDER_TYPE_LABEL[r.reminderType] ?? r.reminderType} · {CHANNEL_LABEL[r.channel] ?? r.channel}
                          {r.recurIntervalDays ? <span className="ml-1 rounded bg-indigo-50 px-1 py-0.5 text-[9px] font-medium text-indigo-600">cada {r.recurIntervalDays}d</span> : null}
                        </p>
                        {r.sourceServiceName && (
                          <p className="text-[10px] text-slate-400">Origen: {r.sourceServiceName}{r.sourcePerformedAt ? ` · ${new Date(r.sourcePerformedAt).toLocaleDateString('es-MX')}` : ''}</p>
                        )}
                        {r.message && <p className="mt-1 line-clamp-2 text-[11px] text-slate-400">{r.message}</p>}
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <ContactActions name={r.customerName} phone={r.customerPhone} email={r.customerEmail}
                            message={r.message} subject={r.subject} />
                          {r.status === 'PENDING' && (
                            <button
                              className="inline-flex items-center gap-1 rounded-lg bg-[var(--brand-primary)] px-2 py-1 text-[11px] font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50"
                              disabled={busyId === r.id} onClick={() => markSent(r.id)}>
                              <Check className="h-3.5 w-3.5" /> Marcar enviado
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// V54 — Insumos (ingredientes) + Recetas (control de inventario a nivel insumo)
// ===========================================================================
const fmtQty = (v: any) => Number(v ?? 0).toLocaleString('es-MX', { maximumFractionDigits: 4 });
const INSUMO_UNITS = ['kg', 'g', 'L', 'ml', 'pza'];

export function InsumosView() {
  const { user } = useAuth();
  const canWrite = can(user, 'product_catalog:write');
  const [rows, setRows] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<any | null>(null);
  const [stockFor, setStockFor] = useState<any | null>(null);
  // §17 Part B/C — insumos POR SUCURSAL. FUENTE ÚNICA (Tarea 3): Insumos sigue la
  // sucursal ACTIVA DE LA SESIÓN (barra superior), igual que Productos/Servicios. Se
  // quitó el selector local, que era una segunda fuente de verdad (podía divergir de
  // la sesión). El backend scopea por la sesión (resolveBranchFilter) al no pasar branchId.
  const [bulkOpen, setBulkOpen] = useState(false);
  // feat/branch-write-require-active — fuente única de la sucursal destino de escritura
  // (hook compartido con Productos/Import). needsChoice = consolidado con >1 sucursal.
  const { branches, writeBranchId, needsChoice } = useActiveWriteBranch();
  // activeBranch: la sucursal destino como objeto (BulkAdjustInsumosModal necesita su nombre).
  const activeBranch = writeBranchId ? branches.find((b) => b.id === writeBranchId) ?? null : null;

  const load = async () => {
    setLoading(true);
    try {
      // Sin branchId: el backend usa la sucursal activa de la sesión (fuente única).
      const [list, oversold] = await Promise.all([
        api(`/insumos?includeInactive=true`) as Promise<any[]>,
        (api(`/insumos/oversold`) as Promise<any[]>).catch(() => [] as any[]),
      ]);
      setRows(list);
      setAlerts(oversold);
    } finally { setLoading(false); }
  };
  // Carga una vez; cambiar la sucursal activa recarga la app (BranchSelector) → remonta.
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const lowCount = useMemo(() => rows.filter((r) => r.isLow && r.isActive).length, [rows]);
  const oversoldRows = useMemo(() => alerts.filter((r) => r.isOversold), [alerts]);

  return (
    <div>
      <Header title="Insumos" subtitle="Ingredientes con inventario a nivel insumo, por sucursal. Vender un producto con receta los descuenta."
        action={canWrite ? (
          <div className="flex flex-wrap items-center gap-2">
            {/* Pista VISIBLE (no solo el title de hover, que en móvil no existe) del
                porqué "Ajuste masivo" está deshabilitado en vista consolidada. Con una
                sola sucursal activeBranch ya la autopreselecciona (arriba), así que ahí
                el botón queda habilitado y esta pista no aparece. */}
            {needsChoice && (
              <span className="text-[11px] text-slate-400">Elige una sucursal en la barra superior para ajustar</span>
            )}
            <button className={btnGhost} disabled={needsChoice} onClick={() => setBulkOpen(true)}
              title={needsChoice ? 'Elige una sucursal concreta en la barra superior para ajustar; en vista consolidada no hay destino único.' : undefined}>
              Ajuste masivo
            </button>
            <button className={btnPrimary} onClick={() => setOpen(true)}>+ Nuevo insumo</button>
          </div>
        ) : undefined} />
      {/* §17 Part B — la existencia es por sucursal. Insumos sigue la sucursal activa de la
          sesión (barra superior), como Productos; sin selector local (Tarea 3, fuente única). */}
      {lowCount > 0 && (
        <p className="mb-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
          ⚠ {lowCount} insumo{lowCount > 1 ? 's' : ''} por debajo de su mínimo de stock.
        </p>
      )}
      {/* Reporte READ-ONLY de SOBREVENTA: insumos cuyo stock quedo en negativo porque
          la receta desconto mas de lo disponible al vender. `overshoot` = cuanto se paso. */}
      {oversoldRows.length > 0 && (
        <div className="mb-4 overflow-hidden rounded-xl border border-rose-200 bg-rose-50">
          <div className="px-4 py-2 text-xs font-semibold text-rose-700">
            🚨 Sobreventa: {oversoldRows.length} insumo{oversoldRows.length > 1 ? 's' : ''} con stock negativo (la receta descontó más de lo disponible).
          </div>
          <table className="w-full text-sm">
            <thead className="bg-rose-100/60 text-left text-[11px] uppercase tracking-wider text-rose-600">
              <tr><th className="px-4 py-2">Insumo</th><th className="px-4 py-2">Stock actual</th><th className="px-4 py-2">Sobrevendido</th></tr>
            </thead>
            <tbody className="divide-y divide-rose-100">
              {oversoldRows.map((i) => (
                <tr key={i.id}>
                  <td className="px-4 py-2 font-medium text-rose-800">{i.name}</td>
                  <td className="px-4 py-2 tabular-nums text-rose-700">{num2(i.stock)} {i.unit}</td>
                  <td className="px-4 py-2 tabular-nums font-semibold text-rose-700">{num2(i.overshoot)} {i.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-2">Insumo</th><th className="px-4 py-2">Unidad</th><th className="px-4 py-2">Stock</th><th className="px-4 py-2">Costo</th><th className="px-4 py-2"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Sin insumos todavía.</td></tr>}
              {rows.map((i) => (
                <tr key={i.id} className={i.isActive ? '' : 'opacity-50'}>
                  <td className="px-4 py-2 font-medium text-slate-800">
                    {i.name}
                    {i.isLow && <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">⚠ bajo</span>}
                  </td>
                  <td className="px-4 py-2 text-slate-500">{i.unit}</td>
                  <td className="px-4 py-2 tabular-nums"><InlineInsumoStock insumo={i} canAdjust={canWrite} writeBranchId={writeBranchId} needsChoice={needsChoice} onSaved={load} /></td>
                  <td className="px-4 py-2 tabular-nums text-slate-500">{i.cost != null ? `$${num2(i.cost)}` : '—'}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    <button className={btnGhost} onClick={() => setStockFor(i)}>Historial</button>
                    {canWrite && <button className={btnGhost} onClick={() => setEdit(i)}>Editar</button>}
                    {canWrite && i.isActive && <button className={btnGhost} onClick={async () => { await api(`/insumos/${i.id}`, { method: 'DELETE' }); load(); }}>Desactivar</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <InsumoModal writeBranchId={writeBranchId} needsChoice={needsChoice} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {edit && <InsumoModal item={edit} writeBranchId={writeBranchId} needsChoice={needsChoice} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {stockFor && <InsumoStockModal insumo={stockFor} canAdjust={canWrite} writeBranchId={writeBranchId} needsChoice={needsChoice} onClose={() => setStockFor(null)} onSaved={load} />}
      {bulkOpen && activeBranch && (
        <BulkAdjustInsumosModal
          insumos={rows.filter((r) => r.isActive)} branch={activeBranch}
          onClose={() => setBulkOpen(false)}
          onSaved={() => { setBulkOpen(false); load(); }} />
      )}
    </div>
  );
}

/**
 * §17 Part C — Ajuste MASIVO de insumos POR SUCURSAL. El admin captura la EXISTENCIA
 * DESEADA (absoluta) de muchos insumos a la vez en la sucursal ACTIVA + un MOTIVO
 * obligatorio (conteo físico / corrección / reparto inicial). El backend genera un
 * movimiento de ajuste por renglón (delta = deseado − actual), auditado con autor.
 *
 * §17 fix 1.1 — la sucursal NO se elige aquí: es la ACTIVA (la que se está viendo),
 * mostrada como texto de solo lectura. Así es imposible escribir en una sucursal
 * distinta de la mostrada (fuente única de verdad, elimina esa clase de error).
 * §17 fix 1.2 — el MOTIVO llega VACÍO (nunca preseleccionado): el usuario lo captura o
 * elige un chip; sin motivo (o en blanco) no se puede guardar.
 */
const BULK_REASON_CHIPS = ['Conteo físico', 'Reparto inicial', 'Corrección', 'Merma', 'Recepción de compra'];
function BulkAdjustInsumosModal({ insumos, branch, onClose, onSaved }:
  { insumos: any[]; branch: { id: string; name: string }; onClose: () => void; onSaved: () => void }) {
  const [reason, setReason] = useState('');
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (!reason.trim()) { setErr('El motivo es obligatorio.'); return; }
    const items = insumos
      .filter((i) => targets[i.id] !== undefined && targets[i.id] !== '')
      .map((i) => ({ insumoId: i.id, target: Number(targets[i.id]) }))
      .filter((it) => Number.isFinite(it.target) && it.target >= 0);
    if (items.length === 0) { setErr('Captura al menos una existencia.'); return; }
    setBusy(true);
    try {
      const res: any = await api('/insumos/bulk-adjust', { method: 'POST', body: { branchId: branch.id, reason: reason.trim(), items } });
      window.alert(`Ajuste aplicado: ${res.adjusted} movimiento(s).`);
      onSaved();
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo aplicar el ajuste'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Ajuste masivo de insumos" onClose={onClose}
      footer={(
        <div className="flex justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
          <button type="button" className={btnPrimary} disabled={busy || !reason.trim()} onClick={submit}>{busy ? 'Aplicando…' : 'Aplicar ajuste'}</button>
        </div>
      )}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <L label="Sucursal">
            {/* §17 fix 1.1 — sucursal ACTIVA, solo lectura (no se puede divergir de lo mostrado). */}
            <div className="input flex items-center bg-slate-50 text-slate-700">{branch.name}</div>
          </L>
          <L label="Motivo (obligatorio)">
            <input className="input" value={reason} autoFocus onChange={(e) => setReason(e.target.value)} placeholder="Escribe el motivo…" />
          </L>
        </div>
        {/* §17 fix 1.2 — chips de sugerencia que el usuario ELIGE activamente (no un default). */}
        <div className="flex flex-wrap gap-1.5">
          {BULK_REASON_CHIPS.map((c) => (
            <button key={c} type="button" onClick={() => setReason(c)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 transition ${reason === c ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'}`}>
              {c}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">Captura la <b>existencia deseada</b> por insumo en <b>{branch.name}</b>. Se deja en blanco lo que no cambia. Cada valor genera un movimiento de ajuste (con tu usuario).</p>
        <div className="max-h-80 overflow-y-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-3 py-2">Insumo</th><th className="px-3 py-2">Unidad</th><th className="px-3 py-2 w-32">Existencia deseada</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {insumos.map((i) => (
                <tr key={i.id}>
                  <td className="px-3 py-1.5 font-medium text-slate-800">{i.name}</td>
                  <td className="px-3 py-1.5 text-slate-500">{i.unit}</td>
                  <td className="px-3 py-1.5">
                    <input className="input py-1" type="number" min="0" step="0.0001" placeholder="—"
                      value={targets[i.id] ?? ''} onChange={(e) => setTargets({ ...targets, [i.id]: e.target.value })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
      </div>
    </Modal>
  );
}

// Edicion agil de stock de insumo (mismo patron que InlineStock de productos): ±1
// rapidos + clic para fijar valor exacto. Cada cambio deja un movimiento (con usuario).
function InlineInsumoStock({ insumo, onSaved, canAdjust = true, writeBranchId = null, needsChoice = false }: { insumo: any; onSaved: () => void; canAdjust?: boolean; writeBranchId?: string | null; needsChoice?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(insumo.stock));
  const [busy, setBusy] = useState(false);
  const low = insumo.isLow;
  // Una edición = una petición (Enter + blur no duplica) y su llave de idempotencia (el backend
  // descarta la 2a petición con la misma llave). Bug de prod 14-oct: "+9" guardado dos veces.
  const sessionRef = useRef<EditSession | null>(null);
  const shownRef = useRef('');

  const apply = async (delta: number, reason: string, adjustmentKey: string) => {
    if (delta === 0) { setEditing(false); return; }
    setBusy(true);
    try { await api(`/insumos/${insumo.id}/adjust-stock`, { method: 'POST', body: { delta, reason, type: 'ajuste', branchId: writeBranchId, adjustmentKey } }); onSaved(); }
    catch (e: any) { window.alert(e?.body?.message || e?.message || 'No se pudo ajustar el stock'); }
    finally { setBusy(false); setEditing(false); }
  };
  const commitEdit = () => {
    sessionRef.current?.commit((key) => {
      const next = Number(val);
      // Sin cambio respecto a lo mostrado (redondeado por unidad) = no se ajusta nada.
      if (!Number.isFinite(next) || val.trim() === shownRef.current) { setEditing(false); return; }
      apply(next - Number(insumo.stock), 'Edición directa', key);
    });
  };
  const quick = (delta: number, reason: string) => { const s = newEditSession(); s.commit((key) => apply(delta, reason, key)); };

  // feat/branch-write-require-active — sin destino único (consolidado + >1 sucursal) el
  // ajuste no puede mandar branchId → stock de solo-lectura (como sin permiso). La pista
  // para elegir sucursal vive en el header.
  if (!canAdjust || needsChoice) {
    return <span className={`tabular-nums ${low ? 'font-semibold text-amber-600' : 'text-slate-700'}`}
      title={needsChoice ? 'Elige una sucursal concreta en la barra superior para ajustar el stock.' : undefined}>{fmtInsumoQty(insumo.stock, insumo.unit)}</span>;
  }
  if (editing) {
    return (
      <input autoFocus type="number" step="any" disabled={busy}
        className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm tabular-nums"
        value={val} onChange={(e) => setVal(e.target.value)} onBlur={commitEdit}
        onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') { sessionRef.current?.cancel(); setEditing(false); } }} />
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <button title="Quitar 1" disabled={busy} onClick={() => quick(-1, 'Ajuste rápido -1')}
        className="h-6 w-6 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">−</button>
      <button title="Editar stock" onClick={() => {
          const shown = String(roundQtyForUnit(insumo.stock, insumo.unit));
          shownRef.current = shown; setVal(shown); sessionRef.current = newEditSession(); setEditing(true);
        }}
        className={`min-w-[2.5rem] tabular-nums ${low ? 'font-semibold text-amber-600' : 'text-slate-700'}`}>{fmtInsumoQty(insumo.stock, insumo.unit)}</button>
      <button title="Agregar 1" disabled={busy} onClick={() => quick(1, 'Ajuste rápido +1')}
        className="h-6 w-6 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">+</button>
    </span>
  );
}

function InsumoModal({ item, writeBranchId = null, needsChoice = false, onClose, onSaved }: { item?: any; writeBranchId?: string | null; needsChoice?: boolean; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!item;
  const [f, setF] = useState({
    name: item?.name ?? '', unit: item?.unit ?? 'pza',
    stock: item?.stock != null ? String(item.stock) : '0',
    lowStockThreshold: item?.lowStockThreshold != null ? String(item.lowStockThreshold) : '',
    cost: item?.cost != null ? String(item.cost) : '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // feat/branch-write-require-active — el stock inicial (>0) es una escritura por sucursal:
  // el backend exige branchId solo si stock > 0. En consolidado (>1 sucursal) con stock > 0
  // no hay destino → se bloquea "Guardar" hasta elegir sucursal (o dejar el stock en 0).
  const blockedByBranch = !isEdit && needsChoice && Number(f.stock) > 0;
  const submit = async (e: any) => {
    e.preventDefault(); setErr(null); setBusy(true);
    const body: any = {
      name: f.name.trim(), unit: f.unit,
      lowStockThreshold: f.lowStockThreshold ? Number(f.lowStockThreshold) : undefined,
      cost: f.cost ? Number(f.cost) : undefined,
    };
    if (!isEdit) {
      body.stock = f.stock ? Number(f.stock) : 0;
      if (body.stock > 0) body.branchId = writeBranchId;
    }
    try {
      if (isEdit) await api(`/insumos/${item.id}`, { method: 'PATCH', body });
      else await api('/insumos', { method: 'POST', body });
      onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); } finally { setBusy(false); }
  };
  return (
    <Modal title={isEdit ? 'Editar insumo' : 'Nuevo insumo'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <L label="Nombre"><input className="input" required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Tortilla, queso…" /></L>
        <div className="grid grid-cols-2 gap-3">
          <L label="Unidad">
            <select className="input" value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })}>
              {INSUMO_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </L>
          {!isEdit && <L label="Stock inicial"><input className="input" type="number" step="any" min="0" value={f.stock} onChange={e => setF({ ...f, stock: e.target.value })} /></L>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <L label="Mínimo de alerta (opcional)"><input className="input" type="number" step="any" min="0" value={f.lowStockThreshold} onChange={e => setF({ ...f, lowStockThreshold: e.target.value })} placeholder="Avisar bajo este nivel" /></L>
          <L label="Costo por unidad (opcional)"><input className="input" type="number" step="0.01" min="0" value={f.cost} onChange={e => setF({ ...f, cost: e.target.value })} /></L>
        </div>
        {isEdit && <p className="text-[11px] text-slate-400">El stock se ajusta desde la lista (±) o en "Historial", así queda el movimiento registrado.</p>}
        {blockedByBranch && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-[11px] text-amber-700 ring-1 ring-amber-200">
            Estás en vista consolidada ("Todas"). Para registrar stock inicial elige una sucursal en la barra superior, o deja el stock inicial en 0.
          </p>
        )}
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex justify-end gap-2"><button type="button" className={btnGhost} onClick={onClose}>Cancelar</button><button className={btnPrimary} disabled={busy || blockedByBranch}
          title={blockedByBranch ? 'Elige una sucursal concreta en la barra superior para registrar el stock inicial; en vista consolidada no hay destino único.' : undefined}>{busy ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear'}</button></div>
      </form>
    </Modal>
  );
}

function InsumoStockModal({ insumo, canAdjust, writeBranchId = null, needsChoice = false, onClose, onSaved }: { insumo: any; canAdjust: boolean; writeBranchId?: string | null; needsChoice?: boolean; onClose: () => void; onSaved: () => void }) {
  const [delta, setDelta] = useState('0');
  const [reason, setReason] = useState('');
  const [type, setType] = useState('ajuste');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [moves, setMoves] = useState<any[]>([]);
  const [loadingMoves, setLoadingMoves] = useState(true);

  const loadMoves = () => {
    setLoadingMoves(true);
    api(`/insumos/${insumo.id}/movements`).then((m) => setMoves(m as any[])).catch(() => setMoves([])).finally(() => setLoadingMoves(false));
  };
  useEffect(() => { loadMoves(); }, [insumo.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const bump = (n: number) => setDelta(String((Number(delta) || 0) + n));
  const preview = Number(insumo.stock || 0) + (Number(delta) || 0);
  const submit = async (e: any) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      await api(`/insumos/${insumo.id}/adjust-stock`, { method: 'POST', body: { delta: Number(delta), reason: reason || undefined, type, branchId: writeBranchId } });
      setDelta('0'); setReason(''); loadMoves(); onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Stock — ${insumo.name}`} onClose={onClose}>
      {/* feat/branch-write-require-active — sin destino único (consolidado + >1 sucursal)
          no se puede ajustar (falta branchId); se muestra solo el historial + pista. */}
      {canAdjust && needsChoice && (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-[11px] text-amber-700 ring-1 ring-amber-200">
          Estás en vista consolidada ("Todas"). Elige una sucursal en la barra superior para ajustar el stock de este insumo.
        </p>
      )}
      {canAdjust && !needsChoice && (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-slate-500">Stock actual: <strong className="tabular-nums">{fmtInsumoQty(insumo.stock, insumo.unit)}</strong> {insumo.unit}</p>
          <div className="flex flex-wrap gap-1.5">{[-10, -5, -1, +1, +5, +10].map(n => <button type="button" key={n} className={btnGhost} onClick={() => bump(n)}>{n > 0 ? `+${n}` : n}</button>)}</div>
          <div className="grid grid-cols-2 gap-3">
            <L label="Delta (+/-)"><input className="input" type="number" step="any" value={delta} onChange={e => setDelta(e.target.value)} /></L>
            <L label="Tipo"><select className="input" value={type} onChange={e => setType(e.target.value)}><option value="ajuste">Ajuste</option><option value="entrada">Entrada (compra/recepción)</option><option value="salida">Salida (merma/baja)</option></select></L>
          </div>
          <p className={`text-sm ${preview < 0 ? 'text-rose-600' : 'text-slate-500'}`}>Nuevo stock: <strong className="tabular-nums">{fmtInsumoQty(preview, insumo.unit)}</strong></p>
          <L label="Motivo (opcional)"><input className="input" value={reason} onChange={e => setReason(e.target.value)} placeholder="Compra, merma, conteo físico…" /></L>
          {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
          <div className="flex justify-end gap-2"><button type="button" className={btnGhost} onClick={onClose}>Cerrar</button><button className={btnPrimary} disabled={busy}>{busy ? 'Guardando…' : 'Aplicar'}</button></div>
        </form>
      )}
      <div className="mt-5 border-t border-slate-100 pt-4">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Historial de movimientos</p>
        {loadingMoves ? <p className="text-sm text-slate-400">Cargando…</p> : moves.length === 0 ? (
          <p className="text-sm text-slate-400">Sin movimientos registrados.</p>
        ) : (
          <div className="max-h-48 space-y-1 overflow-y-auto">
            {moves.map((m) => {
              const d = Number(m.delta);
              return (
                <div key={m.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-2">
                    <span className={`tabular-nums font-medium ${d < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{d > 0 ? `+${fmtQty(d)}` : fmtQty(d)}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{MOVE_TYPE_LABEL[m.type] ?? m.type}</span>
                    <span className="text-slate-500">{m.reason || '—'}</span>
                  </span>
                  <span className="shrink-0 text-[11px] text-slate-400">{m.userName?.trim() || 'Sistema'} · {new Date(m.createdAt).toLocaleString('es-MX')}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Editor de RECETA de un producto (insumos + cantidades por unidad). Se abre desde
 *  la ficha del producto. Guardar reemplaza la receta completa (PUT). */
export function RecipeModal({ product, onClose }: { product: any; onClose: () => void }) {
  const [insumos, setInsumos] = useState<any[]>([]);
  const [lines, setLines] = useState<Array<{ insumoId: string; qty: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Guardarraíl (#7) — avisos de sanidad pendientes de confirmar (no bloquean).
  const [warns, setWarns] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api('/insumos'), api(`/insumos/recipe/${product.id}`)])
      .then(([ins, rec]: any) => {
        if (cancelled) return;
        setInsumos(ins as any[]);
        setLines((rec as any[]).map((r) => ({ insumoId: r.insumoId, qty: String(r.qty) })));
      })
      .catch(() => { if (!cancelled) setErr('No se pudo cargar la receta'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [product.id]);

  const addLine = () => setLines([...lines, { insumoId: insumos[0]?.id ?? '', qty: '1' }]);
  const setLine = (idx: number, patch: any) => setLines(lines.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const removeLine = (idx: number) => setLines(lines.filter((_, i) => i !== idx));
  const unitOf = (id: string) => insumos.find((i) => i.id === id)?.unit ?? '';

  const save = async (force = false) => {
    setErr(null);
    const items = lines.filter((l) => l.insumoId && Number(l.qty) > 0).map((l) => ({ insumoId: l.insumoId, qty: Number(l.qty) }));
    // Guardarraíl (#7) — AVISO de sanidad, no bloqueo: >1 kg/L por unidad, o costo
    // de receta > precio de venta ("¿Seguro? 20 kg de queso por sopa").
    if (!force) {
      const info = items.map((it) => {
        const ins = insumos.find((i) => i.id === it.insumoId);
        return { insumoName: ins?.name ?? '?', unit: ins?.unit ?? '', qty: it.qty, cost: ins?.cost != null ? Number(ins.cost) : null };
      });
      const w = recipeWarnings(info, product?.basePrice != null ? Number(product.basePrice) : null);
      if (w.length) { setWarns(w); return; }
    }
    setWarns(null); setBusy(true);
    try { await api(`/insumos/recipe/${product.id}`, { method: 'PUT', body: { items } }); onClose(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo guardar la receta'); setBusy(false); }
  };

  return (
    <Modal title={`Receta — ${product.name}`} onClose={onClose} maxWidth="max-w-lg">
      <p className="mb-3 text-xs text-slate-500">Define qué insumos consume <strong>una unidad</strong> de este producto. Al venderlo, se descuentan del inventario de insumos.</p>
      {loading ? <p className="text-sm text-slate-400">Cargando…</p> : insumos.length === 0 ? (
        <p className="text-sm text-slate-500">Primero crea insumos en la pestaña <strong>Insumos</strong>.</p>
      ) : (
        <div className="space-y-2">
          {lines.length === 0 && <p className="text-sm text-slate-400">Sin insumos en la receta. Agrega uno.</p>}
          {/* Hallazgo B (08/09 noche): fila extraída a RecipeLineRow — en <640px se
              APILA y el modal nunca desborda (el par "kg o [__] g" cortaba la
              pantalla en celular). Test de render: recipeLineRow.test.tsx. */}
          {lines.map((l, idx) => (
            <RecipeLineRow key={idx} insumoId={l.insumoId} qty={l.qty} insumos={insumos}
              onPick={(insumoId) => setLine(idx, { insumoId })}
              onQty={(qty) => { setWarns(null); setLine(idx, { qty }); }}
              onRemove={() => removeLine(idx)} removeClass={btnGhost} />
          ))}
          {/* Conversión visible por renglón grande: "= descuenta X kg por unidad vendida". */}
          {lines.filter((l) => l.insumoId && Number(l.qty) > 0 && altUnitOf(unitOf(l.insumoId))).map((l, i) => (
            <p key={`conv-${i}`} className="pl-1 text-[11px] text-slate-400">
              {insumos.find((x) => x.id === l.insumoId)?.name}: descuenta <strong>{Number(l.qty)} {unitOf(l.insumoId)}</strong> por unidad vendida
              {Number(l.qty) < 1 ? ` (${Math.round(Number(l.qty) * 1000)} ${altUnitOf(unitOf(l.insumoId))})` : ''}
            </p>
          ))}
          <button type="button" className={btnGhost} onClick={addLine}>+ Agregar insumo</button>
        </div>
      )}
      {err && <p className="alert-over mt-3 rounded-xl px-3 py-2 text-xs">{err}</p>}
      {/* Guardarraíl (#7) — AVISO de sanidad, no bloqueo, sin window.alert. */}
      {warns && (
        <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {warns.map((w, i) => <p key={i} className="font-medium">⚠ {w}</p>)}
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" className={btnGhost} onClick={() => setWarns(null)}>Corregir</button>
            <button type="button" className="rounded-lg border border-amber-400 bg-white px-2.5 py-1 font-semibold text-amber-800 hover:bg-amber-100" onClick={() => save(true)}>
              Sí, guardar así
            </button>
          </div>
        </div>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
        <button type="button" className={btnPrimary} disabled={busy || loading || insumos.length === 0} onClick={() => save()}>{busy ? 'Guardando…' : 'Guardar receta'}</button>
      </div>
    </Modal>
  );
}
